import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOutUser } from '../../../services/auth';
import { auth, db } from '../../../lib/firebase';
import { collection, collectionGroup, getDocs, getDoc, query, where, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getCashbackSettings, updateCashbackSettings, type CashbackSettings, getFeaturedListingIds, updateFeaturedListingIds, getPlatformFeeRate, updatePlatformFeeRate, getSiteSettings, updateSiteSettings, type SocialLinks, type TutorialItem } from '../../../services/settings';
import { uploadTutorialVideo } from '../../../services/storage';
import { getWallet } from '../../../services/wallet';
import { createMarketer } from '../../../services/marketers';
import { createAdminMessage } from '../../../services/adminMessages';
import { adminBroadcastEmail, notifyAdmins, sellerApprovedEmail, sellerRejectedEmail, sendTestEmail } from '../../../services/email';
import { deleteTicketLink, getTicketLinkByListingId } from '../../../services/ticketLinks';

// Helpers for media detection and mapping
const isVideoUrl = (url: string) => /\.(mp4|webm|mov|m4v|mpeg|mpg|mkv)$/i.test(url);

const collectIdMediaUrls = (record: any): string[] => {
  const urls: string[] = [];
  const push = (v: any) => { if (typeof v === 'string' && v) urls.push(v); };
  const pushArr = (arr: any) => { if (Array.isArray(arr)) arr.forEach(push); };

  push(record?.studentIdUrl);
  push(record?.studentIdImageUrl);
  push(record?.idImageUrl);
  push(record?.idUrl);
  push(record?.idMediaUrl);
  push(record?.verificationImage);
  push(record?.verification?.mediaUrl);
  push(record?.verification?.imageUrl);
  pushArr(record?.idImages);
  pushArr(record?.images);

  push(record?.verification?.idUrl);
  pushArr(record?.verification?.idUrls);
  pushArr(record?.verification?.media);
  pushArr(record?.verification?.mediaUrls);
  pushArr(record?.verificationMedia);
  pushArr(record?.studentIdPhotos);
  pushArr(record?.idPhotos);
  push(record?.studentCardUrl);

  return Array.from(new Set(urls));
};

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [sellers, setSellers] = useState<any[]>([]);
  const [buyers, setBuyers] = useState<any[]>([]);
  const [listings, setListings] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [transactionSearch, setTransactionSearch] = useState('');
  const [verifications, setVerifications] = useState<any[]>([]);
  const [sellerSortOrder, setSellerSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [sellerStatusFilter, setSellerStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [sellerSearch, setSellerSearch] = useState('');
  const [verificationStatusFilter, setVerificationStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [verificationSearch, setVerificationSearch] = useState('');
  const [buyerSortOrder, setBuyerSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [listingSortOrder, setListingSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [ordersSortOrder, setOrdersSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [transactionsSortOrder, setTransactionsSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [pendingTransactionsSortOrder, setPendingTransactionsSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [verificationsSortOrder, setVerificationsSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [referralsSortOrder, setReferralsSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [marketersSortOrder, setMarketersSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [adminMessagesSortOrder, setAdminMessagesSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [supportTicketsSortOrder, setSupportTicketsSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [reportsSortOrder, setReportsSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [loadingBuyers, setLoadingBuyers] = useState(false);
  const [loadingListings, setLoadingListings] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [pendingTransactions, setPendingTransactions] = useState<any[]>([]);
  const [loadingPendingTransactions, setLoadingPendingTransactions] = useState(false);
  const [transactionUsers, setTransactionUsers] = useState<any[]>([]);
  const [selectedTransactionUserId, setSelectedTransactionUserId] = useState<string | null>(null);
  const [loadingVerifications, setLoadingVerifications] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState<any>(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  // Add seller profile modal state
  const [showSellerProfileModal, setShowSellerProfileModal] = useState(false);
  // Lightbox state for viewing verification media
  const [showMediaLightbox, setShowMediaLightbox] = useState(false);
  const [mediaLightboxUrls, setMediaLightboxUrls] = useState<string[]>([]);
  const [mediaLightboxIndex, setMediaLightboxIndex] = useState(0);
  const [showSellerSalesModal, setShowSellerSalesModal] = useState(false);
  const [sellerOrders, setSellerOrders] = useState<any[]>([]);
  const [loadingSellerSales, setLoadingSellerSales] = useState(false);
  // Orders tab state
  const [ordersList, setOrdersList] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [orderSearchId, setOrderSearchId] = useState('');
  // Marketers state
  const [marketers, setMarketers] = useState<any[]>([]);
  const [loadingMarketers, setLoadingMarketers] = useState(false);
  const [creatingMarketer, setCreatingMarketer] = useState(false);
  const [newMarketerName, setNewMarketerName] = useState('');
  const [newMarketerEmail, setNewMarketerEmail] = useState('');
  // Admin messages state
  const [adminMessages, setAdminMessages] = useState<any[]>([]);
  const [loadingAdminMessages, setLoadingAdminMessages] = useState(false);
  const [newMessageText, setNewMessageText] = useState('');
  const [newMessageScope, setNewMessageScope] = useState<'all' | 'buyers' | 'sellers' | 'ids'>('all');
  const [newMessageTargetIds, setNewMessageTargetIds] = useState('');
  const [newMessageActive, setNewMessageActive] = useState(true);
  // Email broadcast state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMode, setEmailMode] = useState<'text' | 'html'>('text');
  const [emailText, setEmailText] = useState('');
  const [emailHtml, setEmailHtml] = useState('');
  const [emailScope, setEmailScope] = useState<'all' | 'buyers' | 'sellers' | 'ids' | 'emails'>('all');
  const [emailTargetIds, setEmailTargetIds] = useState('');
  const [emailTargetEmails, setEmailTargetEmails] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const escapeHtml = (s: string) => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const plainTextToHtml = (text: string) => {
    const escaped = escapeHtml(text.trim());
    // Convert double newlines to paragraphs, single newlines to breaks
    const paragraphs = escaped.split(/\n{2,}/).map(p => p.replace(/\n/g, '<br/>'));
    return paragraphs.map(p => `<p>${p}</p>`).join('\n');
  };

  const toMillis = (value: any): number => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const asNumber = Number(value);
      if (Number.isFinite(asNumber)) return asNumber;
      const parsed = Date.parse(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if (typeof value?.toMillis === 'function') return value.toMillis();
    if (typeof value?.seconds === 'number') return value.seconds * 1000;
    return 0;
  };

  const compareMillis = (a: any, b: any, sortOrder: 'newest' | 'oldest') => {
    const diff = toMillis(a) - toMillis(b);
    return sortOrder === 'newest' ? -diff : diff;
  };

  const handleSendBroadcast = async () => {
    const contentValid = emailMode === 'html' ? !!emailHtml.trim() : !!emailText.trim();
    if (!emailSubject.trim() || !contentValid) {
      alert('Subject and message content are required');
      return;
    }
    const finalHtml = emailMode === 'html' ? emailHtml.trim() : plainTextToHtml(emailText);
    setSendingEmail(true);
    try {
      await adminBroadcastEmail({
        subject: emailSubject.trim(),
        html: finalHtml,
        scope: emailScope,
        targetIds: emailScope === 'ids' ? emailTargetIds.split(',').map(id => id.trim()).filter(Boolean) : undefined,
        recipientEmails: emailScope === 'emails' ? emailTargetEmails.split(',').map(e => e.trim()).filter(Boolean) : undefined,
      });
      alert('Email broadcast queued successfully');
      setEmailSubject('');
      setEmailText('');
      setEmailHtml('');
      setEmailScope('all');
      setEmailTargetIds('');
      setEmailTargetEmails('');
      setEmailMode('text');
    } catch (err: any) {
      console.error(err);
      alert(err?.message || 'Failed to send email broadcast');
    } finally {
      setSendingEmail(false);
    }
  };
  // Add buyer profile modal state
  const [selectedBuyer, setSelectedBuyer] = useState<any>(null);
  const [showBuyerProfileModal, setShowBuyerProfileModal] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeSellers: 0,
    pendingVerifications: 0,
    todaysTransactions: 0,
    // Platform money overview (ALL sales: marketplace + tickets)
    totalTransactionVolume: 0, // gross value of all sales (marketplace + tickets)
    platformGain: 0,           // our total profit (all fees earned)
    walletBalanceTotal: 0,     // total currently held in user wallets
    // breakdown
    marketplaceVolume: 0,
    marketplaceProfit: 0,
    ordersCount: 0,
    ticketVolume: 0,
    ticketProfit: 0,
    ticketsSold: 0,            // number of ticket purchases recorded
  });
  // Cashback settings state
  const [cashbackSettings, setCashbackSettings] = useState<CashbackSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  // Platform fee state
  const [platformFeeRate, setPlatformFeeRateState] = useState<number>(0.10);
  // Site settings: social links + tutorial videos
  const [social, setSocial] = useState<SocialLinks>({});
  const [socialSaving, setSocialSaving] = useState(false);
  const [sellerVideoUrl, setSellerVideoUrl] = useState('');
  const [buyerVideoUrl, setBuyerVideoUrl] = useState('');
  const [videoSaving, setVideoSaving] = useState(false);
  const [videoUploadPct, setVideoUploadPct] = useState<{ seller: number | null; buyer: number | null }>({ seller: null, buyer: null });
  const [tutorialItems, setTutorialItems] = useState<TutorialItem[]>([]);
  const [itemUploadPct, setItemUploadPct] = useState<Record<string, number | null>>({});
  const [platformFeeLoading, setPlatformFeeLoading] = useState(false);
  const [platformFeeSaving, setPlatformFeeSaving] = useState(false);
  // Featured listings settings state
  const [featuredIds, setFeaturedIds] = useState<string[]>([]);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [featuredSaving, setFeaturedSaving] = useState(false);
  const [featuredSearch, setFeaturedSearch] = useState('');

  // Support tickets state
  const [supportTickets, setSupportTickets] = useState<any[]>([]);
  const [loadingSupport, setLoadingSupport] = useState(false);
  const [supportQuery, setSupportQuery] = useState('');
  const [reports, setReports] = useState<any[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [supportSubTab, setSupportSubTab] = useState<'support' | 'reports'>('support');
  // Referrals state
  const [referrals, setReferrals] = useState<any[]>([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  // Referrals filters
  const [referralQuery, setReferralQuery] = useState('');
  const [referralDateFrom, setReferralDateFrom] = useState<string>('');
  const [referralDateTo, setReferralDateTo] = useState<string>('');

  const loadSupportFromLocal = (): any[] => {
    try {
      const raw = JSON.parse(localStorage.getItem('supportTickets') || '[]');
      return raw.map((t: any) => ({ ...t, active: t.active ?? true }));
    } catch {
      return [];
    }
  };

  const fetchSupportTickets = async () => {
    setLoadingSupport(true);
    try {
      const res = await fetch('/api/admin/support');
      if (res.ok) {
        const data = await res.json();
        setSupportTickets((data || []).map((t: any) => ({ ...t, active: t.active ?? true })));
      } else {
        setSupportTickets(loadSupportFromLocal());
      }
    } catch {
      setSupportTickets(loadSupportFromLocal());
    } finally {
      setLoadingSupport(false);
    }
  };

  const toggleTicketActive = (id: string, active: boolean) => {
    setSupportTickets(prev => {
      const next = prev.map(t => t.id === id ? { ...t, active } : t);
      try { localStorage.setItem('supportTickets', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const fetchReports = async () => {
    setLoadingReports(true);
    try {
      const res = await fetch('/api/admin/reports');
      if (res.ok) {
        const data = await res.json();
        setReports(Array.isArray(data) ? data : []);
      } else {
        try {
          setReports(JSON.parse(localStorage.getItem('reports') || '[]'));
        } catch {
          setReports([]);
        }
      }
    } catch {
      try {
        setReports(JSON.parse(localStorage.getItem('reports') || '[]'));
      } catch {
        setReports([]);
      }
    } finally {
      setLoadingReports(false);
    }
  };

  // Fetch referrals across users
  const fetchReferrals = async () => {
    setLoadingReferrals(true);
    try {
      const q = query(collectionGroup(db, 'referrals'));
      const snap = await getDocs(q);
      const raw = snap.docs.map(d => {
        const referrerId = d.ref.parent.parent?.id || null;
        const data = d.data() as any;
        return { id: d.id, referrerId, ...data };
      });
      const userIds = Array.from(new Set(raw.flatMap(r => [r.referrerId, r.userId].filter(Boolean))));
      const userSnaps = await Promise.all(userIds.map(uid => getDoc(doc(db, 'users', uid!))));
      const userMap: Record<string, any> = {};
      userSnaps.forEach(s => { if (s.exists()) userMap[s.id] = s.data(); });
      const rows = raw.map(r => ({
        ...r,
        referrerName: userMap[r.referrerId]?.fullName || '',
        referrerEmail: userMap[r.referrerId]?.email || '',
        referrerCode: userMap[r.referrerId]?.referralCode || '',
        referredName: userMap[r.userId]?.fullName || r.name || '',
        referredEmail: userMap[r.userId]?.email || r.email || '',
        createdAt: r.createdAt || null,
      }));
      setReferrals(rows);
    } catch (e) {
      console.error('Error fetching referrals:', e);
    } finally {
      setLoadingReferrals(false);
    }
  };

  const fetchMarketers = async () => {
    try {
      setLoadingMarketers(true);
      const q = query(collection(db, 'marketers'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMarketers(items);
    } catch (e) {
      console.error('Error fetching marketers', e);
      alert('Failed to load marketers');
    } finally {
      setLoadingMarketers(false);
    }
  };

  const fetchAdminMessages = async () => {
    setLoadingAdminMessages(true);
    try {
      const q = query(collection(db, 'admin_messages'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAdminMessages(items);
    } catch (e) {
      console.error('Error fetching admin messages', e);
      alert('Failed to load admin messages');
    } finally {
      setLoadingAdminMessages(false);
    }
  };

  const handleCreateAdminMsg = async () => {
    if (!newMessageText.trim()) {
      alert('Please enter a message');
      return;
    }
    try {
      const payload: any = {
        text: newMessageText.trim(),
        scope: newMessageScope,
        active: newMessageActive,
      };
      if (newMessageScope === 'ids') {
        payload.targetIds = newMessageTargetIds
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);
      }
      await createAdminMessage(payload);
      setNewMessageText('');
      setNewMessageScope('all');
      setNewMessageTargetIds('');
      setNewMessageActive(true);
      await fetchAdminMessages();
      alert('Message created successfully');
    } catch (e: any) {
      console.error('Error creating admin message', e);
      alert('Failed to create message: ' + (e?.message || 'Unknown error'));
    }
  };

  const toggleMessageActive = async (id: string, active: boolean) => {
    try {
      await updateDoc(doc(db, 'admin_messages', id), { active });
      setAdminMessages(prev => prev.map(m => (m.id === id ? { ...m, active } : m)));
    } catch (e) {
      console.error('Error updating message', e);
      alert('Failed to update message status');
    }
  };

  const handleCreateMarketer = async () => {
    if (!newMarketerName.trim()) {
      alert('Please enter a marketer name');
      return;
    }
    setCreatingMarketer(true);
    let marketer: Awaited<ReturnType<typeof createMarketer>> | null = null;
    try {
      marketer = await createMarketer({ name: newMarketerName.trim(), email: newMarketerEmail.trim() || undefined });
      setNewMarketerName('');
      setNewMarketerEmail('');
      await fetchMarketers();
    } catch (e: any) {
      console.error('Error creating marketer', e);
      alert('Failed to create marketer: ' + (e?.message || 'Unknown error'));
      setCreatingMarketer(false);
      return;
    }

    // Marketer is created — clipboard copy is best-effort
    const url = `${window.location.origin}/auth/sign-up?ref=${marketer.referralCode}`;
    let copied = false;
    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        copied = true;
      }
    } catch {
      copied = false;
    }
    if (!copied) {
      // Fallback: legacy execCommand path (works in non-secure contexts / embeds)
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        copied = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        copied = false;
      }
    }

    setCreatingMarketer(false);
    if (copied) {
      alert('Marketer created. Referral link copied to clipboard:\n\n' + url);
    } else {
      window.prompt('Marketer created. Copy this referral link:', url);
    }
  };

  useEffect(() => {
    if (activeTab === 'referrals') fetchReferrals();
    if (activeTab === 'marketers') fetchMarketers();
    if (activeTab === 'featured') loadFeaturedSettings();
    if (activeTab === 'messages') fetchAdminMessages();
    if (activeTab === 'settings') {
      setPlatformFeeLoading(true);
      getPlatformFeeRate().then(r => { setPlatformFeeRateState(r); setPlatformFeeLoading(false); }).catch(() => setPlatformFeeLoading(false));
      getSiteSettings().then((s) => {
        setSocial(s.social || {});
        setSellerVideoUrl(s.tutorials?.sellerVideoUrl || '');
        setBuyerVideoUrl(s.tutorials?.buyerVideoUrl || '');
        setTutorialItems(Array.isArray(s.tutorials?.items) ? s.tutorials!.items! : []);
      }).catch(() => {});
    }
  }, [activeTab]);

  // Fetch pending transactions
  const fetchPendingTransactions = async () => {
    setLoadingPendingTransactions(true);
    try {
      // Pending withdrawal/payout requests live at wallets/{uid}/transactions/*
      const q = query(collectionGroup(db, 'transactions'), where('status', '==', 'pending'));
      const querySnapshot = await getDocs(q);
      const pendingTx: any[] = [];
      querySnapshot.forEach((d) => {
        const walletOwnerId = d.ref.parent.parent?.id || null;
        pendingTx.push({ id: d.id, walletOwnerId, ...d.data() });
      });
      setPendingTransactions(pendingTx);
    } catch (error) {
      console.error("Error fetching pending transactions:", error);
      alert("Failed to fetch pending transactions.");
    } finally {
      setLoadingPendingTransactions(false);
    }
  };

  const handleApproveTransaction = async (transactionId: string, walletOwnerId?: string | null) => {
    if (!walletOwnerId) { alert('Missing wallet owner; cannot approve.'); return; }
    if (!confirm("Are you sure you want to approve this transaction?")) return;
    try {
      await updateDoc(doc(db, 'wallets', walletOwnerId, 'transactions', transactionId), { status: 'completed' });
      notifyAdmins({
        event: 'transaction_approved',
        subject: `Transaction approved: ${transactionId}`,
        text: 'An admin approved a wallet transaction.',
        meta: { transactionId, walletOwnerId },
      });
      alert("Transaction approved successfully!");
      fetchPendingTransactions();
    } catch (error) {
      console.error("Error approving transaction:", error);
      alert("Failed to approve transaction.");
    }
  };

  const handleDeclineTransaction = async (transactionId: string, walletOwnerId?: string | null) => {
    if (!walletOwnerId) { alert('Missing wallet owner; cannot decline.'); return; }
    if (!confirm("Are you sure you want to decline this transaction?")) return;
    try {
      await updateDoc(doc(db, 'wallets', walletOwnerId, 'transactions', transactionId), { status: 'failed' });
      notifyAdmins({
        event: 'transaction_declined',
        subject: `Transaction declined: ${transactionId}`,
        text: 'An admin declined a wallet transaction.',
        meta: { transactionId, walletOwnerId },
      });
      alert("Transaction declined successfully!");
      fetchPendingTransactions();
    } catch (error) {
      console.error("Error declining transaction:", error);
      alert("Failed to decline transaction.");
    }
  };

  useEffect(() => {
    fetchPendingTransactions();
  }, []);

  const getActiveSupportCountFromLocal = (): number => {
    try {
      const data = JSON.parse(localStorage.getItem('supportTickets') || '[]');
      return data.filter((t: any) => t.active !== false).length;
    } catch { return 0; }
  };

  const supportActiveCount = (supportTickets.length ? supportTickets.filter(t => t.active !== false).length : getActiveSupportCountFromLocal());

  useEffect(() => {
    // Check if admin is authenticated via session storage
    const isAuthenticated = sessionStorage.getItem('adminAuthenticated') === 'true';
    if (!isAuthenticated) {
      navigate('/admin/auth/sign-in');
      return;
    }
    
    setIsAdmin(true);
    setLoading(false);
    fetchStats();
    fetchCashbackSettings();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      // Fetch total users
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const totalUsers = usersSnapshot.size;

      // Fetch active sellers
      const sellersQuery = query(usersRef, where('role', '==', 'seller'), where('status', '==', 'approved'));
      const sellersSnapshot = await getDocs(sellersQuery);
      const activeSellers = sellersSnapshot.size;

      // Fetch pending verifications
      const pendingQuery = query(usersRef, where('role', '==', 'seller'), where('status', '==', 'pending'));
      const pendingSnapshot = await getDocs(pendingQuery);
      const pendingVerifications = pendingSnapshot.size;

      // Fetch all wallet transactions (collection group) once: used for today's count
      // AND for summing marketplace platform fees (our profit on item/house sales).
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const cgSnap = await getDocs(query(collectionGroup(db, 'transactions')));
      let todaysTransactions = 0;
      let marketplaceProfit = 0;
      cgSnap.docs.forEach(d => {
        const data: any = d.data();
        const ts = data?.createdAt;
        if (ts && ts.seconds && new Date(ts.seconds * 1000) >= today) todaysTransactions++;
        if (data?.type === 'platform_fee') marketplaceProfit += Number(data?.amount || 0);
      });

      // Ticket sales volume + our ticket fees.
      let ticketVolume = 0;
      let ticketProfit = 0;
      let ticketsSold = 0;
      try {
        const purchasesSnap = await getDocs(collection(db, 'ticket_purchases'));
        ticketsSold = purchasesSnap.size;
        purchasesSnap.docs.forEach(d => {
          const p: any = d.data();
          ticketVolume += Number(p?.totalAmount || 0);
          ticketProfit += Number(p?.platformFee || 0);
        });
      } catch { /* ignore */ }

      // Marketplace (item/house) sales volume — gross value of all non-cancelled orders.
      let marketplaceVolume = 0;
      let ordersCount = 0;
      try {
        const ordersSnap = await getDocs(collection(db, 'orders'));
        ordersSnap.docs.forEach(d => {
          const o: any = d.data();
          if (o?.status === 'cancelled') return;
          ordersCount++;
          marketplaceVolume += Number(o?.totalAmount || 0);
        });
      } catch { /* ignore */ }

      // Total held across all user wallets.
      let walletBalanceTotal = 0;
      try {
        const walletsSnap = await getDocs(collection(db, 'wallets'));
        walletsSnap.docs.forEach(d => {
          const w: any = d.data();
          walletBalanceTotal += Number(w?.balance || 0);
        });
      } catch { /* ignore */ }

      const totalTransactionVolume = marketplaceVolume + ticketVolume;
      const platformGain = marketplaceProfit + ticketProfit;

      setStats({
        totalUsers,
        activeSellers,
        pendingVerifications,
        todaysTransactions,
        totalTransactionVolume,
        platformGain,
        walletBalanceTotal,
        marketplaceVolume,
        marketplaceProfit,
        ordersCount,
        ticketVolume,
        ticketProfit,
        ticketsSold,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCashbackSettings = async () => {
    setSettingsLoading(true);
    try {
      const s = await getCashbackSettings();
      setCashbackSettings(s);
    } catch (e) {
      console.error('Failed to load cashback settings', e);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveCashback = async () => {
    if (!cashbackSettings) return;
    setSettingsSaving(true);
    try {
      await updateCashbackSettings(cashbackSettings);
      alert('Cashback settings saved');
    } catch (e) {
      console.error('Failed to save cashback settings', e);
      alert('Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  // Featured listings settings helpers
  const loadFeaturedSettings = async () => {
    setFeaturedLoading(true);
    try {
      const ids = await getFeaturedListingIds();
      setFeaturedIds(ids);
    } catch (e) {
      console.error('Failed to load featured listings settings', e);
      alert('Failed to load featured listings settings');
    } finally {
      setFeaturedLoading(false);
    }
  };

  const handleSaveFeatured = async () => {
    setFeaturedSaving(true);
    try {
      await updateFeaturedListingIds(featuredIds);
      alert('Featured listings saved');
    } catch (e) {
      console.error('Failed to save featured listings', e);
      alert('Failed to save featured listings');
    } finally {
      setFeaturedSaving(false);
    }
  };

  const handleSaveSocial = async () => {
    setSocialSaving(true);
    try {
      await updateSiteSettings({ social });
      alert('Social links saved — they now show in the site footer.');
    } catch {
      alert('Failed to save social links.');
    } finally {
      setSocialSaving(false);
    }
  };

  const handleVideoFile = async (slot: 'seller' | 'buyer', file: File) => {
    setVideoUploadPct((p) => ({ ...p, [slot]: 0 }));
    try {
      const url = await uploadTutorialVideo(file, slot, (pct) => setVideoUploadPct((p) => ({ ...p, [slot]: pct })));
      if (slot === 'seller') setSellerVideoUrl(url); else setBuyerVideoUrl(url);
    } catch (e: any) {
      console.error('Video upload failed', e);
      alert(`Video upload failed: ${e?.message || e}\n\nTip: paste a YouTube link instead — it's instant and avoids large uploads.`);
    } finally {
      setVideoUploadPct((p) => ({ ...p, [slot]: null }));
    }
  };

  const patchTutorialItem = (id: string, next: Partial<TutorialItem>) =>
    setTutorialItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...next } : it)));

  const addTutorialItem = () =>
    setTutorialItems((prev) => [...prev, { id: `t${Date.now()}`, title: '', description: '', videoUrl: '' }]);

  const removeTutorialItem = (id: string) =>
    setTutorialItems((prev) => prev.filter((it) => it.id !== id));

  const handleItemVideoFile = async (id: string, file: File) => {
    setItemUploadPct((p) => ({ ...p, [id]: 0 }));
    try {
      const url = await uploadTutorialVideo(file, id, (pct) => setItemUploadPct((p) => ({ ...p, [id]: pct })));
      patchTutorialItem(id, { videoUrl: url });
    } catch (e: any) {
      console.error('Video upload failed', e);
      alert(`Video upload failed: ${e?.message || e}\n\nTip: paste a YouTube link instead — it's instant and avoids large uploads.`);
    } finally {
      setItemUploadPct((p) => ({ ...p, [id]: null }));
    }
  };

  const handleSaveTutorials = async () => {
    setVideoSaving(true);
    try {
      const items = tutorialItems
        .map((it) => ({ id: it.id, title: (it.title || '').trim(), description: (it.description || '').trim(), videoUrl: (it.videoUrl || '').trim() }))
        .filter((it) => it.title || it.videoUrl);
      await updateSiteSettings({ tutorials: { sellerVideoUrl: sellerVideoUrl.trim(), buyerVideoUrl: buyerVideoUrl.trim(), items } });
      alert('Tutorials saved — live on the Tutorials page.');
    } catch {
      alert('Failed to save tutorials.');
    } finally {
      setVideoSaving(false);
    }
  };

  const fetchSellers = async () => {
    try {
      setLoadingSellers(true);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('role', '==', 'seller'));
      const querySnapshot = await getDocs(q);
      
      const sellersRaw = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const sellersData = await Promise.all(
        sellersRaw.map(async (s: any) => {
          let walletBalance = 0;
          try {
            const w = await getWallet(s.id);
            walletBalance = w?.balance || 0;
          } catch (e) {
            walletBalance = 0;
          }
          const sellerType = (s.subscription?.plan || s.plan || s.tier || s.type || 'Basic') as string;
          return { ...s, walletBalance, sellerType };
        })
      );
      
      setSellers(sellersData);
    } catch (error) {
      console.error('Error fetching sellers:', error);
    } finally {
      setLoadingSellers(false);
    }
  };

  const handleApproveSeller = async (sellerId: string) => {
    try {
      const sellerRef = doc(db, 'users', sellerId);
      const beforeSnap = await getDoc(sellerRef);
      const beforeData = beforeSnap.exists() ? beforeSnap.data() as any : null;
      await updateDoc(sellerRef, {
        status: 'approved',
        approvalDate: new Date(),
        rejectionReason: null
      });

      // Email the seller and notify admins
      const sellerEmail = beforeData?.email || '';
      const sellerName = beforeData?.fullName || beforeData?.name || '';
      if (sellerEmail) {
        sellerApprovedEmail(sellerEmail, sellerName);
      }
      notifyAdmins({
        event: 'seller_approved',
        subject: `Seller approved: ${sellerName || sellerEmail || sellerId}`,
        text: `An admin approved a seller account.`,
        meta: { sellerId, sellerEmail, sellerName },
      });

      // Refresh sellers list and stats
      fetchSellers();
      fetchStats();
      alert('Seller approved successfully!');
    } catch (error: any) {
      console.error('Error approving seller:', error);
      alert('Error approving seller: ' + (error?.message || 'Unknown error'));
    }
  };

  const handleRejectSeller = async (sellerId: string, reason: string) => {
    try {
      const sellerRef = doc(db, 'users', sellerId);
      const beforeSnap = await getDoc(sellerRef);
      const beforeData = beforeSnap.exists() ? beforeSnap.data() as any : null;
      await updateDoc(sellerRef, {
        status: 'rejected',
        rejectionReason: reason,
        rejectionDate: new Date()
      });

      // Email the seller with reason and notify admins
      const sellerEmail = beforeData?.email || '';
      const sellerName = beforeData?.fullName || beforeData?.name || '';
      if (sellerEmail) {
        sellerRejectedEmail(sellerEmail, sellerName, reason);
      }
      notifyAdmins({
        event: 'seller_rejected',
        subject: `Seller rejected: ${sellerName || sellerEmail || sellerId}`,
        text: `An admin rejected a seller account.`,
        meta: { sellerId, sellerEmail, sellerName, reason },
      });

      // Refresh sellers list and stats
      fetchSellers();
      fetchStats();
      setShowRejectionModal(false);
      setRejectionReason('');
      setSelectedSeller(null);
      alert('Seller rejected successfully!');
    } catch (error: any) {
      console.error('Error rejecting seller:', error);
      alert('Error rejecting seller: ' + (error?.message || 'Unknown error'));
    }
  };

  const openRejectionModal = (seller: any) => {
    setSelectedSeller(seller);
    setShowRejectionModal(true);
  };

  // Add openSellerProfile helper
  const openSellerProfile = (seller: any) => {
    setSelectedSeller(seller);
    setShowSellerProfileModal(true);
  };

  // Add openBuyerProfile helper
  const openBuyerProfile = (buyer: any) => {
    setSelectedBuyer(buyer);
    setShowBuyerProfileModal(true);
  };

  // Add openSellerSales helper
  const openSellerSales = (seller: any) => {
    setSelectedSeller(seller);
    setShowSellerSalesModal(true);
    fetchSellerOrders(seller.id);
  };

  const fetchSellerOrders = async (sellerId: string) => {
    try {
      setLoadingSellerSales(true);
      const q = query(collection(db, 'orders'), where('sellerId', '==', sellerId), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const orders = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSellerOrders(orders);
    } catch (error: any) {
      console.error('Error fetching seller orders:', error);
      alert('Error fetching seller sales: ' + (error?.message || 'Unknown error'));
    } finally {
      setLoadingSellerSales(false);
    }
  };

  const handleDeleteUser = async (userId: string, userName?: string) => {
    // Guard: never let an admin delete their own signed-in account.
    if (auth.currentUser?.uid && userId === auth.currentUser.uid) {
      alert('You cannot delete your own admin account while signed in.');
      return;
    }
    const label = userName ? `"${userName}"` : 'this user';
    if (!confirm(`Are you sure you want to permanently delete ${label}?\n\nThis will delete their profile, wallet, and all listings. This cannot be undone.`)) return;
    try {
      // Delete listings owned by this user
      try {
        const listingsSnap = await getDocs(query(collection(db, 'listings'), where('sellerId', '==', userId)));
        await Promise.all(listingsSnap.docs.map(d => deleteDoc(d.ref)));
      } catch {}

      // Delete wallet doc
      try {
        await deleteDoc(doc(db, 'wallets', userId));
      } catch {}

      // Delete user profile doc
      await deleteDoc(doc(db, 'users', userId));

      fetchSellers();
      fetchBuyers();
      fetchStats();
      alert('User deleted successfully.');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user. Please try again.');
    }
  };

  const fetchBuyers = async () => {
    try {
      setLoadingBuyers(true);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('role', '==', 'buyer'));
      const querySnapshot = await getDocs(q);
      
      const buyersRaw = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const buyersData = await Promise.all(
        buyersRaw.map(async (b: any) => {
          let walletBalance = 0;
          let purchaseCount = 0;
          try {
            const w = await getWallet(b.id);
            walletBalance = w?.balance || 0;
          } catch (e) {
            walletBalance = 0;
          }
          try {
            const ordersQ = query(collection(db, 'orders'), where('buyerId', '==', b.id));
            const ordersSnap = await getDocs(ordersQ);
            purchaseCount = ordersSnap.size;
          } catch (e) {
            purchaseCount = 0;
          }
          return { ...b, walletBalance, purchaseCount };
        })
      );
      
      setBuyers(buyersData);
    } catch (error) {
      console.error('Error fetching buyers:', error);
    } finally {
      setLoadingBuyers(false);
    }
  };

  const fetchListings = async () => {
    setLoadingListings(true);
    try {
      const q = query(collection(db, 'listings'));
      const querySnapshot = await getDocs(q);
      const listingsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setListings(listingsData);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoadingListings(false);
    }
  };

  const handleDeleteListing = async (listingId: string, title: string) => {
    const listing = listings.find(l => l.id === listingId);
    const isTicket = listing?.type === 'ticket';
    const confirmMsg = isTicket
      ? `Delete ticket event "${title}"? This will remove the event, its ticket link, and the listing. This cannot be undone.`
      : `Delete listing "${title}"? This cannot be undone.`;
    if (!confirm(confirmMsg)) return;
    try {
      if (isTicket) {
        // Tickets are a `listings` doc paired with a `ticket_links` doc. Resolve the
        // matching link (by stored listingId, else seller + title) and delete both.
        const link = await getTicketLinkByListingId(listingId, { sellerId: listing?.sellerId, title });
        if (link?.id) {
          await deleteTicketLink(link.id); // removes the ticket_link (and its listing when linked)
        } else {
          console.warn(`No ticket_link found for ticket listing ${listingId}; deleting the listing only.`);
        }
        // Always ensure the listing itself is gone (deleteTicketLink only removes it
        // when the link carried a listingId, and the fallback match may not have one).
        try { await deleteDoc(doc(db, 'listings', listingId)); } catch { /* may already be gone */ }
      } else {
        await deleteDoc(doc(db, 'listings', listingId));
      }
      setListings(prev => prev.filter(l => l.id !== listingId));
      notifyAdmins({
        event: isTicket ? 'ticket_deleted' : 'listing_deleted',
        subject: `${isTicket ? 'Ticket event' : 'Listing'} deleted: ${title || listingId}`,
        text: `An admin deleted a ${isTicket ? 'ticket event' : 'listing'}.`,
        meta: { listingId, title, type: listing?.type || 'listing' },
      });
    } catch (error) {
      console.error('Error deleting listing:', error);
      alert('Failed to delete listing');
    }
  };

  const handleFlagListing = async (listingId: string, currentlyFlagged: boolean) => {
    const reason = currentlyFlagged ? '' : (prompt('Reason for flagging this listing?') || '').trim();
    if (!currentlyFlagged && !reason) return;
    try {
      await updateDoc(doc(db, 'listings', listingId), {
        flagged: !currentlyFlagged,
        flagReason: currentlyFlagged ? '' : reason,
        flaggedAt: currentlyFlagged ? null : new Date(),
      });
      setListings(prev => prev.map(l => l.id === listingId
        ? { ...l, flagged: !currentlyFlagged, flagReason: currentlyFlagged ? '' : reason }
        : l));
      notifyAdmins({
        event: currentlyFlagged ? 'listing_unflagged' : 'listing_flagged',
        subject: currentlyFlagged ? `Listing unflagged: ${listingId}` : `Listing flagged: ${listingId}`,
        text: currentlyFlagged ? 'An admin removed a flag from a listing.' : 'An admin flagged a listing.',
        meta: { listingId, reason: currentlyFlagged ? '' : reason },
      });
    } catch (error) {
      console.error('Error flagging listing:', error);
      alert('Failed to update flag status');
    }
  };

  const fetchTransactions = async () => {
    setLoadingTransactions(true);
    try {
      const q = query(collectionGroup(db, 'transactions'));
      const querySnapshot = await getDocs(q);
      const raw = querySnapshot.docs.map(d => {
        const sellerId = d.ref.parent.parent?.id || null;
        const data = d.data() as any;
        return { id: d.id, ...data, sellerId };
      });

      // Fetch related orders to resolve buyer info
      const orderIds = Array.from(new Set(raw.map(r => r.orderId).filter(Boolean)));
      const orderSnaps = await Promise.all(orderIds.map(id => getDoc(doc(db, 'orders', id))));
      const orderMap: Record<string, any> = {};
      orderSnaps.forEach(s => { if (s.exists()) orderMap[s.id] = s.data(); });

      // Collect user ids (wallet owners + buyers)
      const userIdSet = new Set<string>();
      raw.forEach(r => {
        if (r.sellerId) userIdSet.add(r.sellerId);
        const order = r.orderId ? orderMap[r.orderId] : null;
        if (order?.buyerId) userIdSet.add(order.buyerId);
      });
      const userIds = Array.from(userIdSet);
      const userSnaps = await Promise.all(userIds.map(uid => getDoc(doc(db, 'users', uid))));
      const userMap: Record<string, any> = {};
      userSnaps.forEach(s => { if (s.exists()) userMap[s.id] = s.data(); });

      const transactionsData = raw.map(r => {
        const order = r.orderId ? orderMap[r.orderId] : null;
        const buyerId = order?.buyerId || r.buyerId || null;
        const seller = r.sellerId ? userMap[r.sellerId] : null;
        const buyer = buyerId ? userMap[buyerId] : null;
        const itemName = (order?.orderItems?.[0]?.title) || r.itemName || undefined;
        return {
          ...r,
          buyerId,
          buyerName: (buyer?.name || buyer?.fullName || buyer?.username || 'Unknown'),
          sellerName: (seller?.name || seller?.fullName || seller?.username || 'Unknown'),
          itemName,
        };
      });

      setTransactions(transactionsData);

      // Build user grouping list (grouped by wallet owner)
      const byUserMap = new Map<string, { uid: string; name: string; count: number }>();
      transactionsData.forEach(t => {
        const uid = t.sellerId;
        if (!uid) return;
        const name = t.sellerName || 'Unknown';
        const prev = byUserMap.get(uid);
        if (prev) byUserMap.set(uid, { uid, name, count: prev.count + 1 });
        else byUserMap.set(uid, { uid, name, count: 1 });
      });
      const usersList = Array.from(byUserMap.values()).sort((a,b) => a.name.localeCompare(b.name));
      setTransactionUsers(usersList);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchVerifications = async () => {
    try {
      setLoadingVerifications(true);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('role', '==', 'seller'), where('status', '==', 'pending'));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map((doc) => {
        const u: any = doc.data();
        return {
          id: doc.id,
          userName: u.name || u.fullName || u.username || 'Unknown User',
          userEmail: u.email || '',
          university: u.university || u.school || '',
          idType: u.verification?.idType || 'Student ID',
          status: u.status || 'pending',
          submittedAt: u.verification?.submittedAt || u.createdAt || null,
          verification: u.verification,
          ...u,
        };
      });
      setVerifications(data);
    } catch (error) {
      console.error('Error fetching verifications:', error);
    } finally {
      setLoadingVerifications(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'sellers') {
      fetchSellers();
    } else if (activeTab === 'buyers') {
      fetchBuyers();
    } else if (activeTab === 'listings') {
      fetchListings();
    } else if (activeTab === 'transactions') {
      fetchTransactions();
    } else if (activeTab === 'verifications') {
      fetchVerifications();
    } else if (activeTab === 'marketers') {
      fetchMarketers();
    } else if (activeTab === 'support') {
      fetchSupportTickets();
      fetchReports();
    } else if (activeTab === 'featured') {
      // Load listings so admin can search/add, and current featured settings
      fetchListings();
      loadFeaturedSettings();
    }
  }, [activeTab]);

  const handleSignOut = async () => {
    await signOutUser();
    navigate('/admin/sign-in');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">Campus Market Admin</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {auth.currentUser?.email}
              </span>
              <button
                onClick={handleSignOut}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Media Lightbox Overlay */}
      {showMediaLightbox && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60]">
          <div className="relative w-[90vw] max-w-[900px]">
            <button onClick={() => setShowMediaLightbox(false)} className="absolute top-2 right-2 bg-white text-black px-3 py-1 rounded">Close</button>
            <div className="flex items-center justify-between">
              <button onClick={() => setMediaLightboxIndex((i) => (i > 0 ? i - 1 : mediaLightboxUrls.length - 1))} className="text-white px-3 py-2">◀</button>
              <div className="flex-1 mx-2">
                {isVideoUrl(mediaLightboxUrls[mediaLightboxIndex]) ? (
                  <video src={mediaLightboxUrls[mediaLightboxIndex]} className="w-full max-h-[80vh] object-contain rounded" controls />
                ) : (
                  <img src={mediaLightboxUrls[mediaLightboxIndex]} alt="Media" className="w-full max-h-[80vh] object-contain rounded" />
                )}
              </div>
              <button onClick={() => setMediaLightboxIndex((i) => (i < mediaLightboxUrls.length - 1 ? i + 1 : 0))} className="text-white px-3 py-2">▶</button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <nav className="flex space-x-8 px-6 py-4 border-b border-gray-200 overflow-x-auto whitespace-nowrap">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'sellers', label: 'Sellers' },
              { id: 'buyers', label: 'Buyers' },
              { id: 'listings', label: 'Listings' },
              { id: 'featured', label: 'Featured' },
              { id: 'transactions', label: 'Transactions' },
              { id: 'orders', label: 'Orders' },
              { id: 'verifications', label: 'Verifications' },
              { id: 'referrals', label: 'Referrals' },
              { id: 'marketers', label: 'Marketers' },
              { id: 'messages', label: 'Messages' },
              { id: 'settings', label: 'Settings' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-2 text-sm font-medium rounded-md ${
                  activeTab === tab.id
                    ? 'bg-teal-100 text-teal-800'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
            <button
              onClick={() => setActiveTab('support')}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                activeTab === 'support'
                  ? 'bg-teal-100 text-teal-800'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Support {supportActiveCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center bg-red-100 text-red-700 rounded-full px-2 text-xs">
                  {supportActiveCount}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Dashboard Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Stats Cards */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-bold">U</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                <dd className="text-lg font-medium text-gray-900">{stats.totalUsers.toLocaleString()}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-bold">S</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Sellers</dt>
                <dd className="text-lg font-medium text-gray-900">{stats.activeSellers.toLocaleString()}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-bold">P</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Pending Verifications</dt>
                <dd className="text-lg font-medium text-gray-900">{stats.pendingVerifications.toLocaleString()}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-bold">T</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Today's Transactions</dt>
                <dd className="text-lg font-medium text-gray-900">{stats.todaysTransactions.toLocaleString()}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Platform money overview — ALL sales (marketplace + tickets) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="rounded-lg bg-gradient-to-br from-teal-600 to-teal-700 p-6 text-white shadow-sm">
            <div className="flex items-center gap-2 text-teal-100"><i className="ri-exchange-funds-line text-lg"></i><span className="text-sm font-medium">Total Transactions</span></div>
            <div className="mt-2 text-2xl font-bold">₦{stats.totalTransactionVolume.toLocaleString()}</div>
            <div className="mt-1 text-xs text-teal-100">Marketplace ₦{stats.marketplaceVolume.toLocaleString()} · Tickets ₦{stats.ticketVolume.toLocaleString()}</div>
          </div>

          <div className="rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-700 p-6 text-white shadow-sm">
            <div className="flex items-center gap-2 text-emerald-100"><i className="ri-money-dollar-circle-line text-lg"></i><span className="text-sm font-medium">Our Profit (all fees)</span></div>
            <div className="mt-2 text-2xl font-bold">₦{stats.platformGain.toLocaleString()}</div>
            <div className="mt-1 text-xs text-emerald-100">Marketplace ₦{stats.marketplaceProfit.toLocaleString()} · Tickets ₦{stats.ticketProfit.toLocaleString()}</div>
          </div>

          <div className="rounded-lg bg-gradient-to-br from-indigo-600 to-indigo-700 p-6 text-white shadow-sm">
            <div className="flex items-center gap-2 text-indigo-100"><i className="ri-wallet-3-line text-lg"></i><span className="text-sm font-medium">Held in User Wallets</span></div>
            <div className="mt-2 text-2xl font-bold">₦{stats.walletBalanceTotal.toLocaleString()}</div>
            <div className="mt-1 text-xs text-indigo-100">Total balance across all users</div>
          </div>

          <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-gray-100">
            <div className="flex items-center gap-2 text-gray-500"><i className="ri-pie-chart-2-line text-lg"></i><span className="text-sm font-medium">Volume Breakdown</span></div>
            <div className="mt-2 text-sm text-gray-900">
              <div className="flex items-center justify-between"><span className="text-gray-500"><i className="ri-store-2-line mr-1"></i>{stats.ordersCount.toLocaleString()} orders</span><span className="font-semibold">₦{stats.marketplaceVolume.toLocaleString()}</span></div>
              <div className="mt-1 flex items-center justify-between"><span className="text-gray-500"><i className="ri-ticket-2-line mr-1"></i>{stats.ticketsSold.toLocaleString()} ticket sales</span><span className="font-semibold">₦{stats.ticketVolume.toLocaleString()}</span></div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {activeTab === 'overview' && 'Dashboard Overview'}
            {activeTab === 'sellers' && 'Seller Management'}
            {activeTab === 'buyers' && 'Buyer Management'}
            {activeTab === 'listings' && 'Listing Management'}
            {activeTab === 'featured' && 'Featured Listings'}
            {activeTab === 'transactions' && 'Transaction History'}
            {activeTab === 'orders' && 'Orders Management'}
            {activeTab === 'verifications' && 'Pending Verifications'}
            {activeTab === 'referrals' && 'Referrals'}
            {activeTab === 'support' && 'Support Management'}
            {activeTab === 'marketers' && 'Marketers'}
            {activeTab === 'messages' && 'Admin Messages'}
            {activeTab === 'settings' && 'Platform Settings'}
          </h2>
          
          <div className="text-gray-600">
            {activeTab === 'messages' && (
              <div className="space-y-6">
                <div className="border rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3">Create Admin Message</h3>
                  <div className="space-y-3">
                    <textarea
                      className="w-full border rounded p-2"
                      rows={3}
                      placeholder="Message body"
                      value={newMessageText}
                      onChange={(e) => setNewMessageText(e.target.value)}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="text-sm text-gray-600">Scope</label>
                        <select
                          className="mt-1 w-full border rounded p-2"
                          value={newMessageScope}
                          onChange={(e) => setNewMessageScope(e.target.value as any)}
                        >
                          <option value="all">All users</option>
                          <option value="buyers">Buyers</option>
                          <option value="sellers">Sellers</option>
                          <option value="ids">Specific user IDs</option>
                        </select>
                      </div>
                      {newMessageScope === 'ids' && (
                        <div className="md:col-span-2">
                          <label className="text-sm text-gray-600">Target IDs (comma-separated)</label>
                          <input
                            className="mt-1 w-full border rounded p-2"
                            placeholder="uid1, uid2, ..."
                            value={newMessageTargetIds}
                            onChange={(e) => setNewMessageTargetIds(e.target.value)}
                          />
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={newMessageActive} onChange={(e) => setNewMessageActive(e.target.checked)} />
                        <span>Active</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={handleCreateAdminMsg} className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-md">Create Message</button>
                      <button onClick={fetchAdminMessages} className="border border-gray-300 px-4 py-2 rounded-md">Refresh</button>
                    </div>
                  </div>
                </div>
                {/* Email Message Section */}
                <div className="border rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3">Create Email Message</h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-gray-600">Subject</label>
                        <input
                          className="mt-1 w-full border rounded p-2"
                          placeholder="Email subject"
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Scope</label>
                        <select
                          className="mt-1 w-full border rounded p-2"
                          value={emailScope}
                          onChange={(e) => setEmailScope(e.target.value as any)}
                        >
                          <option value="all">All users</option>
                          <option value="buyers">Buyers</option>
                          <option value="sellers">Sellers</option>
                          <option value="ids">Specific user IDs</option>
                          <option value="emails">Specific emails</option>
                        </select>
                      </div>
                    </div>
                    {emailScope === 'ids' && (
                      <div>
                        <label className="text-sm text-gray-600">Target IDs (comma-separated)</label>
                        <input
                          className="mt-1 w-full border rounded p-2"
                          placeholder="uid1, uid2, ..."
                          value={emailTargetIds}
                          onChange={(e) => setEmailTargetIds(e.target.value)}
                        />
                      </div>
                    )}
                    {emailScope === 'emails' && (
                      <div>
                        <label className="text-sm text-gray-600">Recipient Emails (comma-separated)</label>
                        <input
                          className="mt-1 w-full border rounded p-2"
                          placeholder="user1@example.com, user2@example.com, ..."
                          value={emailTargetEmails}
                          onChange={(e) => setEmailTargetEmails(e.target.value)}
                        />
                      </div>
                    )}
                    {emailMode === 'text' ? (
                      <div>
                        <label className="text-sm text-gray-600">Message (plain text)</label>
                        <textarea
                          className="mt-1 w-full border rounded p-2"
                          rows={6}
                          placeholder="Write your message. New lines will be preserved."
                          value={emailText}
                          onChange={(e) => setEmailText(e.target.value)}
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="text-sm text-gray-600">HTML Content</label>
                        <textarea
                          className="mt-1 w-full border rounded p-2 font-mono"
                          rows={6}
                          placeholder="<h1>Welcome</h1><p>Your message...</p>"
                          value={emailHtml}
                          onChange={(e) => setEmailHtml(e.target.value)}
                        />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={handleSendBroadcast}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
                        disabled={sendingEmail}
                      >
                        {sendingEmail ? 'Sending…' : 'Send Email Broadcast'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold">Messages</h3>
                    <select
                      value={adminMessagesSortOrder}
                      onChange={(e) => setAdminMessagesSortOrder(e.target.value as any)}
                      className="px-3 py-1 border rounded"
                    >
                      <option value="newest">Newest</option>
                      <option value="oldest">Oldest</option>
                    </select>
                  </div>
                  {loadingAdminMessages ? (
                    <p>Loading messages...</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-600">
                            <th className="py-2 pr-4">Text</th>
                            <th className="py-2 pr-4">Scope</th>
                            <th className="py-2 pr-4">Active</th>
                            <th className="py-2 pr-4">Created</th>
                            <th className="py-2 pr-4">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...adminMessages]
                            .sort((a: any, b: any) => compareMillis(a.createdAt, b.createdAt, adminMessagesSortOrder))
                            .map((m: any) => (
                            <tr key={m.id} className="border-t">
                              <td className="py-2 pr-4">{m.text}</td>
                              <td className="py-2 pr-4">{m.scope || 'all'}</td>
                              <td className="py-2 pr-4">
                                <span className={`px-2 py-1 rounded text-xs ${m.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                  {m.active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="py-2 pr-4">{m.createdAt?.seconds ? new Date(m.createdAt.seconds * 1000).toLocaleString() : ''}</td>
                              <td className="py-2 pr-4">
                                <button onClick={() => toggleMessageActive(m.id, !m.active)} className="px-3 py-1 rounded bg-blue-600 text-white">
                                  {m.active ? 'Deactivate' : 'Activate'}
                                </button>
                              </td>
                            </tr>
                          ))}
                          {adminMessages.length === 0 && !loadingAdminMessages && (
                            <tr>
                              <td className="py-3 text-gray-500" colSpan={5}>No messages found.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Platform Commission Fee</h3>
                  <p className="text-sm text-gray-500 mb-4">Percentage deducted from every sale when buyer releases payment to seller.</p>
                  {platformFeeLoading ? (
                    <p className="text-gray-500 text-sm">Loading...</p>
                  ) : (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="50"
                          step="0.5"
                          value={Math.round(platformFeeRate * 100 * 10) / 10}
                          onChange={(e) => setPlatformFeeRateState(Number(e.target.value) / 100)}
                          className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-lg font-semibold text-center"
                        />
                        <span className="text-lg font-semibold text-gray-700">%</span>
                      </div>
                      <button
                        disabled={platformFeeSaving}
                        onClick={async () => {
                          setPlatformFeeSaving(true);
                          try {
                            await updatePlatformFeeRate(platformFeeRate);
                            alert(`Platform fee updated to ${Math.round(platformFeeRate * 1000) / 10}%`);
                          } catch {
                            alert('Failed to save. Please try again.');
                          } finally {
                            setPlatformFeeSaving(false);
                          }
                        }}
                        className="bg-teal-600 text-white px-5 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-60 font-medium"
                      >
                        {platformFeeSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                  <p className="mt-3 text-xs text-gray-400">
                    Current: <strong>{Math.round(platformFeeRate * 1000) / 10}%</strong> — e.g. on a ₦10,000 sale, platform earns ₦{Math.round(10000 * platformFeeRate).toLocaleString()}, seller receives ₦{(10000 - Math.round(10000 * platformFeeRate)).toLocaleString()}.
                  </p>
                </div>

                {/* Social Links */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Social Links</h3>
                  <p className="text-sm text-gray-500 mb-4">Paste your profile URLs. Filled-in ones appear as icons in the site footer.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {([
                      ['instagram', 'Instagram', 'https://instagram.com/…'],
                      ['twitter', 'X (Twitter)', 'https://x.com/…'],
                      ['facebook', 'Facebook', 'https://facebook.com/…'],
                      ['tiktok', 'TikTok', 'https://tiktok.com/@…'],
                      ['youtube', 'YouTube', 'https://youtube.com/@…'],
                      ['linkedin', 'LinkedIn', 'https://linkedin.com/company/…'],
                      ['whatsapp', 'WhatsApp', 'wa.me link or phone number'],
                    ] as [keyof SocialLinks, string, string][]).map(([key, label, ph]) => (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                        <input
                          type="text"
                          value={social[key] || ''}
                          onChange={(e) => setSocial((s) => ({ ...s, [key]: e.target.value }))}
                          placeholder={ph}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={handleSaveSocial}
                    disabled={socialSaving}
                    className="mt-4 bg-teal-600 text-white px-5 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-60 font-medium"
                  >
                    {socialSaving ? 'Saving…' : 'Save Social Links'}
                  </button>
                </div>

                {/* Tutorial Videos */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Tutorial Videos</h3>
                  <p className="text-sm text-gray-500 mb-4">Upload a video file directly, or paste a YouTube link. Shown on the public <strong>/tutorials</strong> page.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {([
                      ['seller', 'Seller registration video', sellerVideoUrl, setSellerVideoUrl],
                      ['buyer', 'Buyer registration video', buyerVideoUrl, setBuyerVideoUrl],
                    ] as ['seller' | 'buyer', string, string, (v: string) => void][]).map(([slot, label, url, setUrl]) => (
                      <div key={slot} className="rounded-lg border border-gray-200 p-4">
                        <label className="block text-sm font-medium text-gray-800 mb-2">{label}</label>
                        {url ? (
                          <video src={url} controls className="mb-3 aspect-video w-full rounded-lg bg-black" />
                        ) : (
                          <div className="mb-3 flex aspect-video w-full items-center justify-center rounded-lg bg-gray-100 text-gray-400"><i className="ri-film-line text-3xl"></i></div>
                        )}
                        <input
                          type="text"
                          value={url}
                          onChange={(e) => setUrl(e.target.value)}
                          placeholder="Paste a YouTube link or video URL"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2"
                        />
                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <i className="ri-upload-2-line"></i> Upload video file
                          <input
                            type="file"
                            accept="video/*"
                            className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleVideoFile(slot, f); if (e.target) e.target.value = ''; }}
                          />
                        </label>
                        {videoUploadPct[slot] !== null && (
                          <div className="mt-2">
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                              <div className="h-full bg-teal-600 transition-all" style={{ width: `${videoUploadPct[slot]}%` }} />
                            </div>
                            <p className="mt-1 text-xs text-gray-500">Uploading… {videoUploadPct[slot]}%</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* More tutorials (add any number) */}
                  <div className="mt-6 border-t border-gray-200 pt-5">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-800">More tutorials</h4>
                      <button onClick={addTutorialItem} className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                        <i className="ri-add-line"></i> Add tutorial
                      </button>
                    </div>
                    {tutorialItems.length === 0 && <p className="text-sm text-gray-400">No extra tutorials yet. Add one — it shows on the Tutorials page below the two above.</p>}
                    <div className="space-y-4">
                      {tutorialItems.map((it) => (
                        <div key={it.id} className="rounded-lg border border-gray-200 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 space-y-2">
                              <input
                                value={it.title}
                                onChange={(e) => patchTutorialItem(it.id, { title: e.target.value })}
                                placeholder="Tutorial title (e.g. How to withdraw)"
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium"
                              />
                              <textarea
                                value={it.description || ''}
                                onChange={(e) => patchTutorialItem(it.id, { description: e.target.value })}
                                placeholder="Short description (optional)"
                                rows={2}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                              />
                            </div>
                            <button onClick={() => removeTutorialItem(it.id)} className="flex-shrink-0 text-gray-400 hover:text-red-500" title="Remove">
                              <i className="ri-delete-bin-line text-lg"></i>
                            </button>
                          </div>
                          {it.videoUrl ? (
                            <video src={it.videoUrl} controls className="mt-3 aspect-video w-full rounded-lg bg-black" />
                          ) : null}
                          <input
                            type="text"
                            value={it.videoUrl || ''}
                            onChange={(e) => patchTutorialItem(it.id, { videoUrl: e.target.value })}
                            placeholder="Paste a YouTube link or video URL"
                            className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          />
                          <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                            <i className="ri-upload-2-line"></i> Upload video file
                            <input type="file" accept="video/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleItemVideoFile(it.id, f); if (e.target) e.target.value = ''; }} />
                          </label>
                          {itemUploadPct[it.id] != null && (
                            <div className="mt-2">
                              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100"><div className="h-full bg-teal-600 transition-all" style={{ width: `${itemUploadPct[it.id]}%` }} /></div>
                              <p className="mt-1 text-xs text-gray-500">Uploading… {itemUploadPct[it.id]}%</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={handleSaveTutorials}
                    disabled={videoSaving || videoUploadPct.seller !== null || videoUploadPct.buyer !== null || Object.values(itemUploadPct).some((v) => v != null)}
                    className="mt-5 bg-teal-600 text-white px-5 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-60 font-medium"
                  >
                    {videoSaving ? 'Saving…' : 'Save Tutorials'}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'marketers' && (
              <div className="space-y-6">
                <div className="border rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3">Create Marketer</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-gray-600">Name</label>
                      <input
                        className="mt-1 w-full border rounded p-2"
                        value={newMarketerName}
                        onChange={(e) => setNewMarketerName(e.target.value)}
                        placeholder="Marketer full name"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Email (optional)</label>
                      <input
                        className="mt-1 w-full border rounded p-2"
                        value={newMarketerEmail}
                        onChange={(e) => setNewMarketerEmail(e.target.value)}
                        placeholder="someone@example.com"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <button onClick={handleCreateMarketer} disabled={creatingMarketer} className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-md">
                      {creatingMarketer ? 'Creating...' : 'Create & Copy Referral Link'}
                    </button>
                    <button onClick={fetchMarketers} className="ml-2 border border-gray-300 px-4 py-2 rounded-md">Refresh</button>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold">Marketers</h3>
                    <select
                      value={marketersSortOrder}
                      onChange={(e) => setMarketersSortOrder(e.target.value as any)}
                      className="px-3 py-1 border rounded"
                    >
                      <option value="newest">Newest</option>
                      <option value="oldest">Oldest</option>
                    </select>
                  </div>
                  {loadingMarketers ? (
                    <p>Loading marketers...</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-600">
                            <th className="py-2 pr-4">Name</th>
                            <th className="py-2 pr-4">Email</th>
                            <th className="py-2 pr-4">Code</th>
                            <th className="py-2 pr-4">Referrals</th>
                            <th className="py-2 pr-4">Created</th>
                            <th className="py-2 pr-4">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...marketers]
                            .sort((a: any, b: any) => compareMillis(a.createdAt, b.createdAt, marketersSortOrder))
                            .map((m: any) => (
                            <tr key={m.id} className="border-t">
                              <td className="py-2 pr-4">{m.name || ''}</td>
                              <td className="py-2 pr-4">{m.email || ''}</td>
                              <td className="py-2 pr-4">{m.referralCode}</td>
                              <td className="py-2 pr-4">{m.referralsCount || 0}</td>
                              <td className="py-2 pr-4">{m.createdAt?.seconds ? new Date(m.createdAt.seconds * 1000).toLocaleString() : ''}</td>
                              <td className="py-2 pr-4">
                                <button
                                  onClick={() => navigator.clipboard.writeText(`${window.location.origin}/auth/sign-up?ref=${m.referralCode}`)}
                                  className="px-3 py-1 rounded bg-blue-600 text-white"
                                >
                                  Copy Link
                                </button>
                              </td>
                            </tr>
                          ))}
                          {marketers.length === 0 && !loadingMarketers && (
                            <tr>
                              <td className="py-3 text-gray-500" colSpan={6}>No marketers found.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'overview' && (
          <div className="space-y-6">
            <p className="text-gray-700">Welcome to the Campus Market Admin Dashboard. Here you can manage all aspects of the platform.</p>

            <EmailTestCard />

            <div className="border rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-2">Cashback Settings</h3>
              {settingsLoading && <p>Loading cashback settings...</p>}
              {!settingsLoading && cashbackSettings && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={cashbackSettings.enabled}
                      onChange={(e) => setCashbackSettings({ ...cashbackSettings, enabled: e.target.checked })}
                    />
                    <span>Enable cashback for buyers</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {cashbackSettings.tiers.map((tier, idx) => (
                      <div key={idx} className="border rounded p-3">
                        <div className="flex gap-2 items-end">
                          <div className="flex-1">
                            <label className="block text-sm text-gray-600">Price upper bound (naira)</label>
                            <input
                              type="number"
                              min={0}
                              className="mt-1 w-full border rounded p-2"
                              value={tier.threshold}
                              onChange={(e) => {
                                const val = parseInt(e.target.value || '0', 10);
                                const tiers = [...cashbackSettings.tiers];
                                tiers[idx] = { ...tiers[idx], threshold: val };
                                setCashbackSettings({ ...cashbackSettings, tiers });
                              }}
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-sm text-gray-600">Cashback amount (naira)</label>
                            <input
                              type="number"
                              min={0}
                              className="mt-1 w-full border rounded p-2"
                              value={tier.amount}
                              onChange={(e) => {
                                const val = parseInt(e.target.value || '0', 10);
                                const tiers = [...cashbackSettings.tiers];
                                tiers[idx] = { ...tiers[idx], amount: val };
                                setCashbackSettings({ ...cashbackSettings, tiers });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600">Announcement message (shows on buyer dashboard)</label>
                    <input
                      type="text"
                      className="mt-1 w-full border rounded p-2"
                      value={cashbackSettings.message || ''}
                      onChange={(e) => setCashbackSettings({ ...cashbackSettings, message: e.target.value })}
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveCashback}
                      className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-60"
                      disabled={settingsSaving}
                    >
                      {settingsSaving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
            {activeTab === 'support' && (
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold mb-4">Support Management</h3>

                {/* Controls: search and sub-tabs */}
                <div className="space-y-2 mb-4">
                  <div>
                    <input
                      type="text"
                      placeholder="Search support/reports..."
                      value={supportQuery}
                      onChange={(e) => setSupportQuery(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
                    />
                  </div>
                  <div className="flex items-center">
                    <div className="inline-flex rounded-md border border-gray-200 bg-white p-1">
                      <button
                        onClick={() => setSupportSubTab('support')}
                        className={`px-2.5 py-1 rounded-sm text-xs ${supportSubTab==='support' ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50'}`}
                      >
                        Support
                      </button>
                      <button
                        onClick={() => setSupportSubTab('reports')}
                        className={`px-2.5 py-1 rounded-sm text-xs ${supportSubTab==='reports' ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50'}`}
                      >
                        Reports
                      </button>
                    </div>
                  </div>
                </div>

                {/* Support Tickets Panel */}
                {supportSubTab === 'support' && (
                  <>
                    {loadingSupport ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-2 text-gray-600">Loading support tickets...</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center mb-4">
                          <div className="flex space-x-2">
                            <button className="px-3 py-1 bg-blue-600 text-white rounded">All</button>
                            <button className="px-3 py-1 bg-gray-200 rounded">Active</button>
                            <button className="px-3 py-1 bg-gray-200 rounded">Inactive</button>
                          </div>
                          <select
                            value={supportTicketsSortOrder}
                            onChange={(e) => setSupportTicketsSortOrder(e.target.value as any)}
                            className="px-3 py-1 border rounded"
                          >
                            <option value="newest">Newest</option>
                            <option value="oldest">Oldest</option>
                          </select>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {[...supportTickets]
                            .filter(t => {
                              const q = supportQuery.trim().toLowerCase();
                              if (!q) return true;
                              return [t.name, t.email, t.message].some(v => (v || '').toLowerCase().includes(q));
                            })
                            .sort((a: any, b: any) => compareMillis(a.createdAt, b.createdAt, supportTicketsSortOrder))
                            .map(t => (
                              <div key={t.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="font-medium text-gray-900">{t.name}</div>
                                  <div className="text-xs text-gray-600">{new Date(t.createdAt).toLocaleString()}</div>
                                </div>
                                <div className="text-xs text-blue-700 mb-2">{t.email}</div>
                                <div className="text-sm text-gray-800 mb-3 whitespace-pre-wrap">{t.message}</div>
                                <div className="flex items-center justify-between">
                                  <span className={`px-2 py-1 rounded text-xs ${t.active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                    {t.active !== false ? 'Active' : 'Inactive'}
                                  </span>
                                  <button
                                    onClick={() => toggleTicketActive(t.id, t.active === false)}
                                    className={`px-3 py-1 rounded ${t.active !== false ? 'bg-gray-200 text-gray-800' : 'bg-green-600 text-white'}`}
                                  >
                                    {t.active !== false ? 'Mark Inactive' : 'Mark Active'}
                                  </button>
                                </div>
                              </div>
                            ))}
                          {supportTickets.length === 0 && (
                            <div className="text-center text-gray-600">No support messages yet.</div>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Reports Panel */}
                {supportSubTab === 'reports' && (
                  <>
                    {loadingReports ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-2 text-gray-600">Loading reports...</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-end mb-3">
                          <select
                            value={reportsSortOrder}
                            onChange={(e) => setReportsSortOrder(e.target.value as any)}
                            className="px-3 py-1 border rounded"
                          >
                            <option value="newest">Newest</option>
                            <option value="oldest">Oldest</option>
                          </select>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[...reports]
                          .filter(r => {
                            const q = supportQuery.trim().toLowerCase();
                            if (!q) return true;
                            return [r.name, r.email, r.reason, r.type, r.targetId || ''].some(v => (v || '').toLowerCase().includes(q));
                          })
                          .sort((a: any, b: any) => compareMillis(a.createdAt, b.createdAt, reportsSortOrder))
                          .map(r => (
                            <div key={r.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <div className="font-medium text-gray-900">{r.name}</div>
                                <div className="text-xs text-gray-600">{new Date(r.createdAt).toLocaleString()}</div>
                              </div>
                              <div className="text-xs text-gray-600 mb-1">Type: <span className="font-medium text-gray-900">{r.type}</span></div>
                              {r.targetId && (
                                <div className="text-xs text-gray-600 mb-1">Target ID: <span className="font-medium text-gray-900">{r.targetId}</span></div>
                              )}
                              <div className="text-xs text-blue-700 mb-2">{r.email}</div>
                              <div className="text-sm text-gray-800 whitespace-pre-wrap">{r.reason}</div>
                            </div>
                          ))}
                        {reports.length === 0 && (
                          <div className="text-center text-gray-600">No reports yet.</div>
                        )}
                      </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'featured' && (
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold mb-4">Featured Listings Control</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Search by title or ID..."
                      value={featuredSearch}
                      onChange={(e) => setFeaturedSearch(e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                    />
                    <button
                      onClick={handleSaveFeatured}
                      disabled={featuredSaving}
                      className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-60"
                    >
                      {featuredSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>

                  <div className="border rounded-lg p-3">
                    <h4 className="text-sm font-medium mb-2">Current Featured ({featuredIds.length})</h4>
                    {featuredLoading ? (
                      <div className="text-gray-600">Loading featured settings…</div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {featuredIds.length === 0 ? (
                          <div className="text-gray-600">No featured listings selected.</div>
                        ) : (
                          featuredIds.map(fid => {
                            const item = listings.find(l => l.id === fid);
                            return (
                              <div key={fid} className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded p-2">
                                <div className="w-14 h-14 rounded overflow-hidden bg-white">
                                  {item?.images?.[0] || item?.image ? (
                                    /\.(mp4|webm|mov|m4v|mpeg|mpg|mkv)$/i.test((item?.images?.[0] || item?.image) || '')
                                      ? <video src={(item?.images?.[0] || item?.image) || ''} className="w-14 h-14 object-cover" muted playsInline />
                                      : <img src={(item?.images?.[0] || item?.image) || ''} alt={item?.title || fid} className="w-14 h-14 object-cover" />
                                  ) : <div className="w-14 h-14 bg-gray-200" />}
                                </div>
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900">{item?.title || fid}</div>
                                  <div className="text-xs text-gray-600">{item?.category || '—'}</div>
                                </div>
                                <button
                                  onClick={() => setFeaturedIds(prev => prev.filter(id => id !== fid))}
                                  className="px-2 py-1 bg-red-600 text-white rounded text-xs"
                                >
                                  Remove
                                </button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>

                  <div className="border rounded-lg p-3">
                    <h4 className="text-sm font-medium mb-2">Search Listings</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {listings
                        .filter(l => {
                          const q = featuredSearch.trim().toLowerCase();
                          if (!q) return true;
                          return (l.title || '').toLowerCase().includes(q) || (l.id || '').toLowerCase().includes(q);
                        })
                        .slice(0, 20)
                        .map(l => {
                          const selected = featuredIds.includes(l.id);
                          return (
                            <div key={l.id} className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded p-2">
                              <div className="w-14 h-14 rounded overflow-hidden bg-white">
                                {l.images?.[0] || l.image ? (
                                  /\.(mp4|webm|mov|m4v|mpeg|mpg|mkv)$/i.test((l.images?.[0] || l.image) || '')
                                    ? <video src={(l.images?.[0] || l.image) || ''} className="w-14 h-14 object-cover" muted playsInline />
                                    : <img src={(l.images?.[0] || l.image) || ''} alt={l.title} className="w-14 h-14 object-cover" />
                                ) : <div className="w-14 h-14 bg-gray-200" />}
                              </div>
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">{l.title}</div>
                                <div className="text-xs text-gray-600">{l.category || '—'}</div>
                              </div>
                              <button
                                onClick={() => setFeaturedIds(prev => selected ? prev : [...prev, l.id!])}
                                disabled={selected}
                                className={`px-2 py-1 rounded text-xs ${selected ? 'bg-gray-300 text-gray-700' : 'bg-green-600 text-white'}`}
                              >
                                {selected ? 'Added' : 'Add'}
                              </button>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'sellers' && (
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold mb-4">Seller Management</h3>
                
                {loadingSellers ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading sellers...</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex space-x-2">
                        {(['all', 'pending', 'approved', 'rejected'] as const).map((f) => (
                          <button
                            key={f}
                            onClick={() => setSellerStatusFilter(f)}
                            className={`px-3 py-1 rounded capitalize ${sellerStatusFilter === f ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                          >
                            {f === 'all' ? 'All' : f}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={sellerSortOrder}
                          onChange={(e) => setSellerSortOrder(e.target.value as any)}
                          className="px-3 py-1 border rounded"
                        >
                          <option value="newest">Newest</option>
                          <option value="oldest">Oldest</option>
                        </select>
                        <input
                          type="text"
                          value={sellerSearch}
                          onChange={(e) => setSellerSearch(e.target.value)}
                          placeholder="Search sellers..."
                          className="px-3 py-1 border rounded"
                        />
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white text-xs md:text-sm">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="px-4 py-2 text-left">Name</th>
                            <th className="px-4 py-2 text-left hidden md:table-cell">Email</th>
                            <th className="px-4 py-2 text-left hidden md:table-cell">University</th>
                            <th className="px-4 py-2 text-left">Status</th>
                            <th className="px-4 py-2 text-left">Type</th>
                            <th className="px-4 py-2 text-left hidden md:table-cell">Joined</th>
                            <th className="px-4 py-2 text-left">Wallet</th>
                            <th className="px-4 py-2 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const q = sellerSearch.trim().toLowerCase();
                            const visible = sellers.filter((seller) => {
                              const status = seller.status || 'pending';
                              if (sellerStatusFilter !== 'all' && status !== sellerStatusFilter) return false;
                              if (!q) return true;
                              const blob = `${seller.displayName || ''} ${seller.email || ''} ${seller.university || ''}`.toLowerCase();
                              return blob.includes(q);
                            });
                            if (visible.length === 0) {
                              return (
                                <tr>
                                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                                    No sellers found
                                  </td>
                                </tr>
                              );
                            }
                            const sorted = [...visible].sort((a, b) => compareMillis(a.createdAt, b.createdAt, sellerSortOrder));
                            return sorted.map((seller) => (
                                <tr key={seller.id} className={seller.status === 'pending' ? 'bg-yellow-50' : seller.status === 'approved' ? 'bg-green-50' : seller.status === 'rejected' ? 'bg-red-50' : 'bg-gray-50'}>
                                  <td className="px-4 py-2">{seller.displayName || 'N/A'}</td>
                                  <td className="px-4 py-2 hidden md:table-cell">{seller.email}</td>
                                  <td className="px-4 py-2 hidden md:table-cell">{seller.university || 'Not specified'}</td>
                                  <td className="px-4 py-2">
                                    <span className={`px-2 py-1 rounded text-xs ${
                                      seller.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                      seller.status === 'approved' ? 'bg-green-100 text-green-800' :
                                      seller.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {seller.status || 'pending'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2">{seller.sellerType || 'Basic'}</td>
                                  <td className="px-4 py-2 hidden md:table-cell">
                                    {seller.createdAt ? 
                                      (seller.createdAt.seconds ? 
                                        new Date(seller.createdAt.seconds * 1000).toLocaleDateString() : 
                                        new Date(seller.createdAt).toLocaleDateString()
                                      ) : 'N/A'}
                                  </td>
                                  <td className="px-4 py-2">₦{(seller.walletBalance || 0).toLocaleString()}</td>
                                  <td className="px-4 py-2 space-x-2">
                                    <button 
                                      onClick={() => {
                                        // View profile functionality
                                        // console.log('View profile:', seller.id);
                                        openSellerProfile(seller);
                                      }}
                                      className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 text-xs"
                                    >
                                      View Profile
                                    </button>
                                    {seller.status === 'pending' && (
                                      <>
                                        <button 
                                          onClick={() => handleApproveSeller(seller.id)}
                                          className="bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 text-xs"
                                        >
                                          Approve
                                        </button>
                                        <button 
                                          onClick={() => openRejectionModal(seller)}
                                          className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 text-xs"
                                        >
                                          Reject
                                        </button>
                                      </>
                                    )}
                                    {seller.status === 'approved' && (
                                      <button onClick={() => openSellerSales(seller)} className="bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700 text-xs">
                                        View Sales
                                      </button>
                                    )}
                                    {seller.status === 'rejected' && (
                                      <button 
                                        onClick={() => {
                                          // Show rejection reason
                                          alert(`Rejection Reason: ${seller.rejectionReason || 'No reason provided'}`);
                                        }}
                                        className="bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700 text-xs"
                                      >
                                        View Reason
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleDeleteUser(seller.id, seller.displayName || seller.email)}
                                      className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 text-xs"
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ));
                            })()}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
            {activeTab === 'buyers' && (
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold mb-4">Buyer Management</h3>
                
                {loadingBuyers ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading buyers...</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex space-x-2">
                        <button className="px-3 py-1 bg-blue-600 text-white rounded">All Buyers</button>
                        <button className="px-3 py-1 bg-gray-200 rounded">Active</button>
                        <button className="px-3 py-1 bg-gray-200 rounded">Inactive</button>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={buyerSortOrder}
                          onChange={(e) => setBuyerSortOrder(e.target.value as any)}
                          className="px-3 py-1 border rounded"
                        >
                          <option value="newest">Newest</option>
                          <option value="oldest">Oldest</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Search buyers..."
                          className="px-3 py-1 border rounded"
                        />
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white text-xs md:text-sm">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="px-4 py-2 text-left">Name</th>
                            <th className="px-4 py-2 text-left hidden md:table-cell">Email</th>
                            <th className="px-4 py-2 text-left hidden md:table-cell">University</th>
                            <th className="px-4 py-2 text-left hidden md:table-cell">Purchases</th>
                            <th className="px-4 py-2 text-left hidden md:table-cell">Joined</th>
                            <th className="px-4 py-2 text-left">Status</th>
                            <th className="px-4 py-2 text-left">Wallet</th>
                            <th className="px-4 py-2 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {buyers.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                                No buyers found
                              </td>
                            </tr>
                          ) : (
                            (() => {
                              const sorted = [...buyers].sort((a, b) => compareMillis(a.createdAt, b.createdAt, buyerSortOrder));
                              return sorted.map((buyer) => (
                                <tr key={buyer.id} className="bg-gray-50">
                                  <td className="px-4 py-2">{buyer.displayName || buyer.fullName || 'N/A'}</td>
                                  <td className="px-4 py-2 hidden md:table-cell">{buyer.email}</td>
                                  <td className="px-4 py-2 hidden md:table-cell">{buyer.university || 'Not specified'}</td>
                                  <td className="px-4 py-2 hidden md:table-cell">{buyer.purchaseCount || 0}</td>
                                  <td className="px-4 py-2 hidden md:table-cell">
                                    {buyer.createdAt ? 
                                      (buyer.createdAt.seconds ? 
                                        new Date(buyer.createdAt.seconds * 1000).toLocaleDateString() : 
                                        new Date(buyer.createdAt).toLocaleDateString()
                                      ) : 'N/A'}
                                  </td>
                                  <td className="px-4 py-2">
                                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                                      Active
                                    </span>
                                  </td>
                                  <td className="px-4 py-2">₦{(buyer.walletBalance || 0).toLocaleString()}</td>
                                  <td className="px-4 py-2 space-x-2">
                                    <button onClick={() => openBuyerProfile(buyer)} className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 text-xs">
                                      View Profile
                                    </button>
                                    <button
                                      onClick={() => handleDeleteUser(buyer.id, buyer.displayName || buyer.fullName || buyer.email)}
                                      className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 text-xs"
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ));
                            })()
                          )}
                        </tbody>
                      </table>
                    </div>
                    
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">Buyer Statistics</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="text-center">
                          <div className="text-lg font-bold text-blue-600">{buyers.length}</div>
                          <div className="text-blue-700">Total Buyers</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-green-600">
                            {buyers.filter(b => b.purchaseCount > 0).length}
                          </div>
                          <div className="text-green-700">Active Buyers</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-purple-600">
                            {buyers.reduce((total, b) => total + (b.purchaseCount || 0), 0)}
                          </div>
                          <div className="text-purple-700">Total Purchases</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-gray-600">
                            {buyers.filter(b => !b.university).length}
                          </div>
                          <div className="text-gray-700">No University</div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            {activeTab === 'listings' && (
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold mb-4">Listing Management</h3>
                
                {loadingListings ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading listings...</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex space-x-2">
                        <button className="px-3 py-1 bg-blue-600 text-white rounded">All Listings</button>
                        <button className="px-3 py-1 bg-gray-200 rounded">Active</button>
                        <button className="px-3 py-1 bg-gray-200 rounded">Sold</button>
                        <button className="px-3 py-1 bg-gray-200 rounded">Expired</button>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={listingSortOrder}
                          onChange={(e) => setListingSortOrder(e.target.value as any)}
                          className="px-3 py-1 border rounded"
                        >
                          <option value="newest">Newest</option>
                          <option value="oldest">Oldest</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Search listings..."
                          className="px-3 py-1 border rounded"
                        />
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white text-xs md:text-sm">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="px-4 py-2 text-left">Title</th>
                            <th className="px-4 py-2 text-left">Thumbnail</th>
                            <th className="px-4 py-2 text-left hidden md:table-cell">Seller</th>
                            <th className="px-4 py-2 text-left">Price</th>
                            <th className="px-4 py-2 text-left hidden md:table-cell">Category</th>
                            <th className="px-4 py-2 text-left">Status</th>
                            <th className="px-4 py-2 text-left hidden md:table-cell">Created</th>
                            <th className="px-4 py-2 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {listings.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                                No listings found
                              </td>
                            </tr>
                          ) : (
                            (() => {
                              const sorted = [...listings].sort((a, b) => compareMillis(a.createdAt, b.createdAt, listingSortOrder));
                              return sorted.map((listing) => (
                                <tr key={listing.id} className="bg-gray-50">
                                  <td className="px-4 py-2 font-medium">{listing.title}</td>
                                  <td className="px-4 py-2">
                                    {(listing.images?.[0] || listing.image) ? (
                                      /\.(mp4|webm|mov|m4v|mpeg|mpg|mkv)$/i.test((listing.images?.[0] || listing.image)) ? (
                                        <video src={(listing.images?.[0] || listing.image)} className="w-16 h-16 object-cover rounded" muted playsInline />
                                      ) : (
                                        <img src={(listing.images?.[0] || listing.image)} alt={listing.title} className="w-16 h-16 object-cover rounded" />
                                      )
                                    ) : (
                                      <div className="w-16 h-16 bg-gray-200 rounded" />
                                    )}
                                  </td>
                                  <td className="px-4 py-2 hidden md:table-cell">{listing.sellerName || 'Unknown'}</td>
                                  <td className="px-4 py-2">₦{listing.price?.toLocaleString()}</td>
                                  <td className="px-4 py-2 hidden md:table-cell">{listing.category || 'Uncategorized'}</td>
                                  <td className="px-4 py-2">
                                    <div className="flex flex-col gap-1">
                                      <span className={`px-2 py-1 rounded text-xs w-fit ${
                                        listing.status === 'active' ? 'bg-green-100 text-green-800' :
                                        listing.status === 'sold' ? 'bg-blue-100 text-blue-800' :
                                        listing.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                                        'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {listing.status || 'active'}
                                      </span>
                                      {listing.flagged && (
                                        <span
                                          className="px-2 py-1 rounded text-xs w-fit bg-red-100 text-red-800"
                                          title={listing.flagReason || 'Flagged'}
                                        >
                                          🚩 Flagged
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 hidden md:table-cell">
                                    {listing.createdAt ? new Date(listing.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                                  </td>
                                  <td className="px-4 py-2">
                                    <div className="flex flex-wrap gap-1">
                                      <button
                                        onClick={() => navigate(`/product/${listing.id}`)}
                                        className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 text-xs"
                                      >
                                        View
                                      </button>
                                      <button
                                        onClick={() => handleFlagListing(listing.id, !!listing.flagged)}
                                        className={`px-2 py-1 rounded text-xs text-white ${
                                          listing.flagged ? 'bg-gray-600 hover:bg-gray-700' : 'bg-yellow-600 hover:bg-yellow-700'
                                        }`}
                                      >
                                        {listing.flagged ? 'Unflag' : 'Flag'}
                                      </button>
                                      <button
                                        onClick={() => handleDeleteListing(listing.id, listing.title)}
                                        className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 text-xs"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ));
                            })()
                          )}
                        </tbody>
                      </table>
                    </div>
                    
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">Listing Statistics</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="text-center">
                          <div className="text-lg font-bold text-blue-600">{listings.length}</div>
                          <div className="text-blue-700">Total Listings</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-green-600">
                            {listings.filter(l => l.status === 'active').length}
                          </div>
                          <div className="text-green-700">Active Listings</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-purple-600">
                            {listings.filter(l => l.status === 'sold').length}
                          </div>
                          <div className="text-purple-700">Sold Listings</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-gray-600">
                            {listings.filter(l => l.status === 'expired').length}
                          </div>
                          <div className="text-gray-700">Expired Listings</div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            {activeTab === 'orders' && (
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold mb-4">Orders</h3>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={orderSearchId}
                      onChange={(e) => setOrderSearchId(e.target.value)}
                      placeholder="Search by Order ID"
                      className="px-3 py-1 border rounded"
                    />
                    <button
                      className="px-3 py-1 bg-blue-600 text-white rounded"
                      onClick={async () => {
                        if (!orderSearchId.trim()) return;
                        try {
                          const s = await getDoc(doc(db, 'orders', orderSearchId.trim()));
                          if (s.exists()) {
                            setOrdersList([{ id: s.id, ...(s.data() as any) }]);
                          } else {
                            alert('Order not found');
                          }
                        } catch (e: any) {
                          alert('Failed to search order: ' + (e?.message || 'Unknown error'));
                        }
                      }}
                    >Search</button>
                    <button
                      className="px-3 py-1 bg-gray-200 rounded"
                      onClick={async () => {
                        setLoadingOrders(true);
                        try {
                          const q = query(collection(db, 'orders'));
                          const snap = await getDocs(q);
                          const list = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
                          list.sort((a:any,b:any) => ((b.createdAt?.seconds)||0) - ((a.createdAt?.seconds)||0));
                          setOrdersList(list);
                        } catch (e) {
                          console.error('Failed to load orders', e);
                          alert('Failed to load orders');
                        } finally {
                          setLoadingOrders(false);
                        }
                      }}
                    >Load Recent</button>
                  </div>
                  <select
                    value={ordersSortOrder}
                    onChange={(e) => setOrdersSortOrder(e.target.value as any)}
                    className="px-3 py-1 border rounded"
                  >
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                  </select>
                </div>
                {loadingOrders ? (
                  <div className="text-center py-8">Loading orders...</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left">Order ID</th>
                          <th className="px-4 py-2 text-left">Buyer</th>
                          <th className="px-4 py-2 text-left">Seller</th>
                          <th className="px-4 py-2 text-left">Amount</th>
                          <th className="px-4 py-2 text-left">Status</th>
                          <th className="px-4 py-2 text-left">Date</th>
                          <th className="px-4 py-2 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ordersList.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-4 py-8 text-center text-gray-500">No orders</td>
                          </tr>
                        ) : (
                          [...ordersList]
                            .sort((a: any, b: any) => compareMillis(a.createdAt, b.createdAt, ordersSortOrder))
                            .map((o:any) => (
                            <tr key={o.id} className="bg-gray-50">
                              <td className="px-4 py-2 font-mono text-sm">{o.id}</td>
                              <td className="px-4 py-2">{o.buyerId || 'Unknown'}</td>
                              <td className="px-4 py-2">{o.sellerId || 'Unknown'}</td>
                              <td className="px-4 py-2">₦{Number(o.totalAmount || o.amount || 0).toLocaleString()}</td>
                              <td className="px-4 py-2">{o.status || 'N/A'}</td>
                              <td className="px-4 py-2">{o.createdAt?.seconds ? new Date(o.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}</td>
                              <td className="px-4 py-2">
                                <button className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700" onClick={() => navigate(`/order/${o.id}`)}>View</button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            {activeTab === 'transactions' && (
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold mb-4">Transaction Management</h3>
                
                {loadingTransactions ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading transactions...</p>
                  </div>
                ) : (
                  <>
              {/* User grouping and filter */}
              <div className="mb-4">
                <div className="flex flex-wrap gap-2 items-center">
                  {transactionUsers.map(u => (
                    <button
                      key={u.uid}
                      className={`px-3 py-1 rounded border ${selectedTransactionUserId === u.uid ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-100 text-gray-800 border-gray-300'}`}
                      onClick={() => setSelectedTransactionUserId(u.uid)}
                      title={`${u.name} — ${u.count} transactions`}
                    >
                      {u.name} <span className="ml-1 text-xs opacity-75">({u.count})</span>
                    </button>
                  ))}
                  {selectedTransactionUserId && (
                    <button
                      className="ml-2 px-3 py-1 rounded bg-gray-200"
                      onClick={() => setSelectedTransactionUserId(null)}
                    >
                      Clear filter
                    </button>
                  )}
                </div>
              </div>

              {/* Existing status filters and search remain */}
              <div className="flex justify-between items-center mb-4">
                <div className="flex space-x-2">
                  <button className="px-3 py-1 bg-blue-600 text-white rounded">All Transactions</button>
                  <button className="px-3 py-1 bg-gray-200 rounded">Completed</button>
                  <button className="px-3 py-1 bg-gray-200 rounded">Pending</button>
                  <button className="px-3 py-1 bg-gray-200 rounded">Failed</button>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={transactionsSortOrder}
                    onChange={(e) => setTransactionsSortOrder(e.target.value as any)}
                    className="px-3 py-1 border rounded"
                  >
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Search transactions..."
                    value={transactionSearch}
                    onChange={(e) => setTransactionSearch(e.target.value)}
                    className="px-3 py-1 border rounded"
                  />
                </div>
              </div>

              {/* Pending Transactions Section */}
              <div className="flex items-center justify-between mb-3 mt-6">
                <h4 className="text-lg font-semibold">Pending Transactions</h4>
                <select
                  value={pendingTransactionsSortOrder}
                  onChange={(e) => setPendingTransactionsSortOrder(e.target.value as any)}
                  className="px-3 py-1 border rounded"
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                </select>
              </div>
              {loadingPendingTransactions ? (
                <div className="text-center py-8">Loading pending transactions...</div>
              ) : (
                <div className="overflow-x-auto mb-8">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-4 py-2 text-left">Transaction ID</th>
                        <th className="px-4 py-2 text-left">Type</th>
                        <th className="px-4 py-2 text-left">Amount</th>
                        <th className="px-4 py-2 text-left">Status</th>
                        <th className="px-4 py-2 text-left">Date</th>
                        <th className="px-4 py-2 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingTransactions.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-gray-500">No pending transactions</td>
                        </tr>
                      ) : (
                        [...pendingTransactions]
                          .sort((a: any, b: any) => compareMillis(a.createdAt, b.createdAt, pendingTransactionsSortOrder))
                          .map((transaction: any) => (
                          <tr key={transaction.id} className="bg-yellow-50">
                            <td className="px-4 py-2 font-mono text-sm">{String(transaction.id || '').substring(0, 8)}...</td>
                            <td className="px-4 py-2">{transaction.type || 'N/A'}</td>
                            <td className="px-4 py-2 font-medium">₦{Number(transaction.amount || 0).toLocaleString()}</td>
                            <td className="px-4 py-2">
                              <span className="px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800">
                                {transaction.status || 'pending'}
                              </span>
                            </td>
                            <td className="px-4 py-2">{
                              transaction.createdAt && transaction.createdAt.seconds
                                ? new Date(transaction.createdAt.seconds * 1000).toLocaleDateString()
                                : 'N/A'
                            }</td>
                            <td className="px-4 py-2 space-x-2">
                              <button
                                onClick={() => handleApproveTransaction(transaction.id, transaction.walletOwnerId || transaction.sellerId)}
                                className="bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 text-xs"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleDeclineTransaction(transaction.id, transaction.walletOwnerId || transaction.sellerId)}
                                className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 text-xs"
                              >
                                Decline
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Filtered table */}
              {(() => {
                const displayed = (
                  selectedTransactionUserId
                    ? transactions.filter(t => t.sellerId === selectedTransactionUserId)
                    : transactions
                ).filter((t: any) => {
                  const q = transactionSearch.trim().toLowerCase();
                  if (!q) return true;
                  return (
                    String(t.id || '').toLowerCase().includes(q) ||
                    String(t.orderId || '').toLowerCase().includes(q) ||
                    String(t.buyerName || '').toLowerCase().includes(q) ||
                    String(t.sellerName || '').toLowerCase().includes(q) ||
                    String(t.itemName || '').toLowerCase().includes(q)
                  );
                });
                const sortedDisplayed = [...displayed].sort((a: any, b: any) => compareMillis(a.createdAt, b.createdAt, transactionsSortOrder));
                return (
                  <>
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="px-4 py-2 text-left">Transaction ID</th>
                            <th className="px-4 py-2 text-left">Buyer</th>
                            <th className="px-4 py-2 text-left">Seller</th>
                            <th className="px-4 py-2 text-left">Amount</th>
                            <th className="px-4 py-2 text-left">Item</th>
                            <th className="px-4 py-2 text-left">Status</th>
                            <th className="px-4 py-2 text-left">Date</th>
                            <th className="px-4 py-2 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayed.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="px-4 py-8 text-center text-gray-500">No transactions found</td>
                            </tr>
                          ) : (
                            sortedDisplayed.map((transaction: any) => (
                              <tr key={transaction.id} className="bg-gray-50">
                                <td className="px-4 py-2 font-mono text-sm">{String(transaction.id || '').substring(0, 8)}...</td>
                                <td className="px-4 py-2">{transaction.buyerName || 'Unknown'}</td>
                                <td className="px-4 py-2">{transaction.sellerName || 'Unknown'}</td>
                                <td className="px-4 py-2 font-medium">₦{Number(transaction.amount || 0).toLocaleString()}</td>
                                <td className="px-4 py-2">{transaction.itemName || 'Unknown Item'}</td>
                                <td className="px-4 py-2">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                                    transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                    transaction.status === 'failed' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {transaction.status || 'pending'}
                                  </span>
                                </td>
                                <td className="px-4 py-2">{
                                  transaction.createdAt && transaction.createdAt.seconds
                                    ? new Date(transaction.createdAt.seconds * 1000).toLocaleDateString()
                                    : 'N/A'
                                }</td>
                                <td className="px-4 py-2">
                                  {transaction.orderId ? (
                                    <button
                                      className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 mr-2"
                                      onClick={() => navigate(`/order/${transaction.orderId}`)}
                                    >
                                      View Order
                                    </button>
                                  ) : (
                                    <button className="bg-gray-200 text-gray-700 px-2 py-1 rounded mr-2" disabled>View</button>
                                  )}
                                  {transaction.type === 'payout_request' && transaction.status === 'pending' && (
                                    <button
                                      className="bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700"
                                      onClick={async () => {
                                        try {
                                          const ref = String(transaction.reference || '');
                                          if (!ref) { alert('No reference on transaction'); return; }
                                          const resp = await fetch(`/api/payouts/status?reference=${encodeURIComponent(ref)}`);
                                          const data = await resp.json().catch(() => ({}));
                                          const status = (data?.data?.status || data?.status || '').toLowerCase();
                                          const txDoc = doc(db, 'wallets', String(transaction.sellerId || ''), 'transactions', String(transaction.id));
                                          if (status === 'success' || status === 'completed') {
                                            await updateDoc(txDoc, { status: 'completed' });
                                          } else if (status) {
                                            await updateDoc(txDoc, { status });
                                          }
                                          alert('Status checked' + (status ? `: ${status}` : ''));
                                        } catch (e: any) {
                                          alert('Failed to check status: ' + (e?.message || 'Unknown error'));
                                        }
                                      }}
                                    >
                                      Check Status
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Statistics based on displayed set */}
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">Transaction Statistics</h4>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div className="text-center">
                          <div className="text-lg font-bold text-blue-600">{displayed.length}</div>
                          <div className="text-blue-700">Total Transactions</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-green-600">{displayed.filter((t: any) => t.status === 'completed').length}</div>
                          <div className="text-green-700">Completed</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-yellow-600">{displayed.filter((t: any) => t.status === 'pending').length}</div>
                          <div className="text-yellow-700">Pending</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-red-600">{displayed.filter((t: any) => t.status === 'failed').length}</div>
                          <div className="text-red-700">Failed</div>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <div className="text-center">
                          <div className="text-xl font-bold text-purple-600">
                            ₦{displayed.filter((t: any) => t.status === 'completed').reduce((total: number, t: any) => total + (t.amount || 0), 0).toLocaleString()}
                          </div>
                          <div className="text-purple-700">Total Revenue</div>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
                  </>
                )}
              </div>
            )}
            {activeTab === 'verifications' && (
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold mb-4">Verification Management</h3>
                
                {loadingVerifications ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading verifications...</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex space-x-2">
                        {(['all', 'pending', 'approved', 'rejected'] as const).map((f) => (
                          <button
                            key={f}
                            onClick={() => setVerificationStatusFilter(f)}
                            className={`px-3 py-1 rounded capitalize ${verificationStatusFilter === f ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                          >
                            {f === 'all' ? 'All Verifications' : f}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={verificationsSortOrder}
                          onChange={(e) => setVerificationsSortOrder(e.target.value as any)}
                          className="px-3 py-1 border rounded"
                        >
                          <option value="newest">Newest</option>
                          <option value="oldest">Oldest</option>
                        </select>
                        <input
                          type="text"
                          value={verificationSearch}
                          onChange={(e) => setVerificationSearch(e.target.value)}
                          placeholder="Search verifications..."
                          className="px-3 py-1 border rounded"
                        />
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white">
                        <thead>
                          <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left">User</th>
                          <th className="px-4 py-2 text-left">Email</th>
                          <th className="px-4 py-2 text-left">University</th>
                          <th className="px-4 py-2 text-left">ID Type</th>
                          <th className="px-4 py-2 text-left">ID Media</th>
                          <th className="px-4 py-2 text-left">Status</th>
                          <th className="px-4 py-2 text-left">Submitted</th>
                          <th className="px-4 py-2 text-left">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const q = verificationSearch.trim().toLowerCase();
                            const visible = verifications.filter((verification) => {
                              const status = verification.status || 'pending';
                              if (verificationStatusFilter !== 'all' && status !== verificationStatusFilter) return false;
                              if (!q) return true;
                              const blob = `${verification.userName || ''} ${verification.userEmail || ''} ${verification.university || ''} ${verification.idType || ''}`.toLowerCase();
                              return blob.includes(q);
                            });
                            if (visible.length === 0) {
                              return (
                                <tr>
                                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                                    No verifications found
                                  </td>
                                </tr>
                              );
                            }
                            const sorted = [...visible].sort((a, b) =>
                              compareMillis(a.submittedAt || a.createdAt, b.submittedAt || b.createdAt, verificationsSortOrder)
                            );
                            return sorted.map((verification) => (
                              <tr key={verification.id} className="bg-gray-50">
                                <td className="px-4 py-2 font-medium">{verification.userName || 'Unknown User'}</td>
                                <td className="px-4 py-2">{verification.userEmail || 'N/A'}</td>
                                <td className="px-4 py-2">{verification.university || 'Not specified'}</td>
                                <td className="px-4 py-2">{verification.idType || 'Student ID'}</td>
<td className="px-4 py-2">
  {(() => {
    const mediaUrls = collectIdMediaUrls(verification);
    const url = mediaUrls[0];
    if (!url) return <span className="text-gray-400">No media</span>;
    return (
      <div className="flex items-center space-x-2">
        {mediaUrls.slice(0, 3).map((u, idx) => (
          <button key={u} onClick={() => { setMediaLightboxUrls(mediaUrls); setMediaLightboxIndex(idx); setShowMediaLightbox(true); }}>
            {isVideoUrl(u) ? (
              <video src={u} className="w-14 h-10 object-cover rounded" muted playsInline />
            ) : (
              <img src={u} alt="ID" className="w-14 h-10 object-cover rounded" />
            )}
          </button>
        ))}
      </div>
    );
  })()}
</td>
                                <td className="px-4 py-2">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    verification.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                    verification.status === 'approved' ? 'bg-green-100 text-green-800' :
                                    verification.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {verification.status || 'pending'}
                                  </span>
                                </td>
                                <td className="px-4 py-2">
                                  {verification.submittedAt ? new Date(verification.submittedAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                                </td>
                                <td className="px-4 py-2">
                                  {verification.status === 'pending' && (
                                    <>
                                      <button onClick={() => handleApproveSeller(verification.id)} className="bg-green-600 text-white px-2 py-1 rounded mr-2 hover:bg-green-700">
                                        Approve
                                      </button>
                                      <button onClick={() => openRejectionModal(verification)} className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700">
                                        Reject
                                      </button>
                                    </>
                                  )}
                                  {verification.status !== 'pending' && (
                                    <button onClick={() => openSellerProfile(verification)} className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700">
                                      View
                                    </button>
                                  )}
                                </td>
                              </tr>
                              ));
                            })()}
                        </tbody>
                      </table>
                    </div>
                    
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">Verification Statistics</h4>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div className="text-center">
                          <div className="text-lg font-bold text-blue-600">{verifications.length}</div>
                          <div className="text-blue-700">Total Verifications</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-yellow-600">
                            {verifications.filter(v => v.status === 'pending').length}
                          </div>
                          <div className="text-yellow-700">Pending</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-green-600">
                            {verifications.filter(v => v.status === 'approved').length}
                          </div>
                          <div className="text-green-700">Approved</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-red-600">
                            {verifications.filter(v => v.status === 'rejected').length}
                          </div>
                          <div className="text-red-700">Rejected</div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'referrals' && (
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold mb-4">Referral Management</h3>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <select
                    value={referralsSortOrder}
                    onChange={(e) => setReferralsSortOrder(e.target.value as any)}
                    className="px-3 py-2 border rounded"
                  >
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                  </select>
                  <input
                    type="text"
                    value={referralQuery}
                    onChange={(e) => setReferralQuery(e.target.value)}
                    placeholder="Search by name, email, code"
                    className="px-3 py-2 border rounded w-64"
                  />
                  <input
                    type="date"
                    value={referralDateFrom}
                    onChange={(e) => setReferralDateFrom(e.target.value)}
                    className="px-3 py-2 border rounded"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="date"
                    value={referralDateTo}
                    onChange={(e) => setReferralDateTo(e.target.value)}
                    className="px-3 py-2 border rounded"
                  />
                </div>
                {loadingReferrals ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading referrals...</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white text-xs md:text-sm">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left">Referrer</th>
                          <th className="px-4 py-2 text-left hidden md:table-cell">Referrer Email</th>
                          <th className="px-4 py-2 text-left hidden md:table-cell">Referral Code</th>
                          <th className="px-4 py-2 text-left">Referred User</th>
                          <th className="px-4 py-2 text-left hidden md:table-cell">Referred Email</th>
                          <th className="px-4 py-2 text-left">Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referrals.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-gray-500">No referral records found</td>
                          </tr>
                        ) : (
                          (() => {
                            const fromTs = referralDateFrom ? new Date(referralDateFrom).getTime() : 0;
                            const toTs = referralDateTo ? new Date(referralDateTo).getTime() : Number.MAX_SAFE_INTEGER;
                            const q = referralQuery.trim().toLowerCase();
                            const visible = referrals.filter((r: any) => {
                              const created = r.createdAt?.toMillis ? r.createdAt.toMillis() : (r.createdAt ? Number(r.createdAt) : 0);
                              if (created && (created < fromTs || created > toTs)) return false;
                              if (!q) return true;
                              const blob = `${r.referrerName || ''} ${r.referrerEmail || ''} ${r.referrerCode || ''} ${r.referredName || ''} ${r.referredEmail || ''}`.toLowerCase();
                              return blob.includes(q);
                            });
                            const sorted = [...visible].sort((a: any, b: any) => compareMillis(a.createdAt, b.createdAt, referralsSortOrder));
                            return sorted.map((r: any) => (
                              <tr key={r.id} className="border-b">
                                <td className="px-4 py-2">{r.referrerName || r.referrerId}</td>
                                <td className="px-4 py-2 hidden md:table-cell">{r.referrerEmail || '-'}</td>
                                <td className="px-4 py-2 hidden md:table-cell">{r.referrerCode || '-'}</td>
                                <td className="px-4 py-2">{r.referredName || r.userId}</td>
                                <td className="px-4 py-2 hidden md:table-cell">{r.referredEmail || '-'}</td>
                                <td className="px-4 py-2 text-gray-600">{r.createdAt ? new Date(r.createdAt.toMillis ? r.createdAt.toMillis() : r.createdAt).toLocaleString() : '-'}</td>
                              </tr>
                            ));
                          })()
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Seller Profile Modal */}
      {showSellerProfileModal && selectedSeller && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[500px] max-w-[95vw]">

            {/* Media Lightbox Overlay */}
            {showMediaLightbox && (
              <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60]">
                <div className="relative w-[90vw] max-w-[900px]">
                  <button onClick={() => setShowMediaLightbox(false)} className="absolute top-2 right-2 bg-white text-black px-3 py-1 rounded">Close</button>
                  <div className="flex items-center justify-between">
                    <button onClick={() => setMediaLightboxIndex((i) => (i > 0 ? i - 1 : mediaLightboxUrls.length - 1))} className="text-white px-3 py-2">◀</button>
                    <div className="flex-1 mx-2">
                      {isVideoUrl(mediaLightboxUrls[mediaLightboxIndex]) ? (
                        <video src={mediaLightboxUrls[mediaLightboxIndex]} className="w-full max-h-[80vh] object-contain rounded" controls />
                      ) : (
                        <img src={mediaLightboxUrls[mediaLightboxIndex]} alt="Media" className="w-full max-h-[80vh] object-contain rounded" />
                      )}
                    </div>
                    <button onClick={() => setMediaLightboxIndex((i) => (i < mediaLightboxUrls.length - 1 ? i + 1 : 0))} className="text-white px-3 py-2">▶</button>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Seller Profile</h3>
              <button
                onClick={() => { setShowSellerProfileModal(false); setSelectedSeller(null); }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-semibold">
                  {(selectedSeller?.displayName || selectedSeller?.fullName || selectedSeller?.email || 'N/A').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-base font-medium text-gray-900">
                    {selectedSeller?.displayName || selectedSeller?.fullName || 'N/A'}
                  </div>
                  <div className="text-gray-600">{selectedSeller?.email}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-gray-500">University</div>
                  <div className="text-gray-900">{selectedSeller?.university || 'Not specified'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Phone</div>
                  <div className="text-gray-900">{selectedSeller?.phone || 'Not specified'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Joined</div>
                  <div className="text-gray-900">
                    {selectedSeller?.createdAt ? (
                      selectedSeller?.createdAt?.seconds ?
                        new Date(selectedSeller.createdAt.seconds * 1000).toLocaleDateString() :
                        new Date(selectedSeller.createdAt).toLocaleDateString()
                    ) : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Status</div>
                  <div className="text-gray-900">{selectedSeller?.status || 'N/A'}</div>
                </div>
              </div>

              {selectedSeller?.wallet && (
                <div className="mt-2">
                  <div className="text-gray-500 mb-1">Wallet</div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-gray-500">Balance</div>
                      <div className="text-gray-900">₦{Number(selectedSeller.wallet.balance || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Escrow</div>
                      <div className="text-gray-900">₦{Number(selectedSeller.wallet.escrowBalance || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Total Earned</div>
                      <div className="text-gray-900">₦{Number(selectedSeller.wallet.totalEarned || 0).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Verification Media Preview */}
              {(() => {
                const urls = collectIdMediaUrls(selectedSeller);
                if (!urls.length) return null;
                return (
                  <div className="mt-4">
                    <div className="text-gray-500 mb-2">Verification Media</div>
                    <div className="grid grid-cols-3 gap-3">
                      {urls.slice(0, 6).map((u, idx) => (
                        <button key={u} onClick={() => { setMediaLightboxUrls(urls); setMediaLightboxIndex(idx); setShowMediaLightbox(true); }} className="block">
                          {isVideoUrl(u) ? (
                            <video src={u} className="w-full h-24 object-cover rounded" muted playsInline />
                          ) : (
                            <img src={u} alt="ID Media" className="w-full h-24 object-cover rounded" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => { setShowSellerProfileModal(false); setSelectedSeller(null); }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Seller Sales Modal */}
      {showSellerSalesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[700px] max-w-[95vw]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Seller Sales</h3>
              <button
                onClick={() => { setShowSellerSalesModal(false); setSellerOrders([]); }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {loadingSellerSales ? (
              <div className="text-center py-10 text-gray-600">Loading sales...</div>
            ) : sellerOrders.length === 0 ? (
              <div className="text-center py-10 text-gray-600">No sales found for this seller.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Order ID</th>
                      <th className="px-3 py-2 text-left">Amount</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sellerOrders.map((o: any) => (
                      <tr key={o.id} className="border-t">
                        <td className="px-3 py-2">{o.id}</td>
                        <td className="px-3 py-2">₦{Number(o.totalAmount || o.amount || 0).toLocaleString()}</td>
                        <td className="px-3 py-2">{o.status || 'N/A'}</td>
                        <td className="px-3 py-2">
                          {o.createdAt?.seconds ?
                            new Date(o.createdAt.seconds * 1000).toLocaleString() :
                            (o.createdAt ? new Date(o.createdAt).toLocaleString() : 'N/A')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => { setShowSellerSalesModal(false); setSellerOrders([]); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Buyer Profile Modal */}
      {showBuyerProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[500px] max-w-[95vw]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Buyer Profile</h3>
              <button
                onClick={() => { setShowBuyerProfileModal(false); setSelectedBuyer(null); }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold">
                  {(selectedBuyer?.displayName || selectedBuyer?.fullName || selectedBuyer?.email || 'N/A').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-base font-medium text-gray-900">
                    {selectedBuyer?.displayName || selectedBuyer?.fullName || 'N/A'}
                  </div>
                  <div className="text-gray-600">{selectedBuyer?.email}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-gray-500">University</div>
                  <div className="text-gray-900">{selectedBuyer?.university || 'Not specified'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Phone</div>
                  <div className="text-gray-900">{selectedBuyer?.phone || 'Not specified'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Joined</div>
                  <div className="text-gray-900">
                    {selectedBuyer?.createdAt ? (
                      selectedBuyer?.createdAt?.seconds ?
                        new Date(selectedBuyer.createdAt.seconds * 1000).toLocaleDateString() :
                        new Date(selectedBuyer.createdAt).toLocaleDateString()
                    ) : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Purchases</div>
                  <div className="text-gray-900">{selectedBuyer?.purchaseCount || 0}</div>
                </div>
              </div>

              {selectedBuyer?.wallet && (
                <div className="mt-2">
                  <div className="text-gray-500 mb-1">Wallet</div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-gray-500">Balance</div>
                      <div className="text-gray-900">₦{Number(selectedBuyer.wallet.balance || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Escrow</div>
                      <div className="text-gray-900">₦{Number(selectedBuyer.wallet.escrowBalance || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Total Spend</div>
                      <div className="text-gray-900">₦{Number(selectedBuyer.wallet.totalSpent || 0).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => { setShowBuyerProfileModal(false); setSelectedBuyer(null); }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Reject Seller Application</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejecting {selectedSeller?.displayName || selectedSeller?.email}'s application:
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full p-3 border border-gray-300 rounded-md mb-4 h-24"
              required
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectionModal(false);
                  setRejectionReason('');
                  setSelectedSeller(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRejectSeller(selectedSeller.id, rejectionReason)}
                disabled={!rejectionReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Small card on the admin overview to verify Resend is sending mail.
// Sends to a custom address you type, or to ADMIN_NOTIFY_EMAILS / the two
// hardcoded admins when blank.
const EmailTestCard: React.FC = () => {
  const [testTo, setTestTo] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const send = async () => {
    setSending(true);
    setResult(null);
    const r = await sendTestEmail(testTo.trim() || undefined);
    if (r.ok) {
      setResult({
        ok: true,
        msg: `Sent via ${r.provider || 'resend'}. id: ${r.id || 'n/a'} · to: ${(r.recipients || []).join(', ') || 'admins'} · from: ${r.from || 'configured FROM'}`,
      });
    } else {
      setResult({ ok: false, msg: r.error || 'Unknown error sending test email.' });
    }
    setSending(false);
  };

  return (
    <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
      <h3 className="text-lg font-semibold mb-1 text-blue-900">Resend / Email — sanity check</h3>
      <p className="text-sm text-blue-800 mb-3">
        Send a test email to confirm Resend is configured and admin notifications are reaching you.
        Leave the input blank to send to the admin notification list (default: mrbodabobo@gmail.com, testyemma123@gmail.com).
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          value={testTo}
          onChange={(e) => setTestTo(e.target.value)}
          placeholder="recipient@example.com (optional)"
          className="flex-1 px-3 py-2 border border-blue-300 rounded bg-white"
        />
        <button
          onClick={send}
          disabled={sending}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-60"
        >
          {sending ? 'Sending…' : 'Send test email'}
        </button>
      </div>
      {result && (
        <p className={`mt-2 text-sm ${result.ok ? 'text-green-700' : 'text-red-700'}`}>
          {result.ok ? '✅ ' : '❌ '}{result.msg}
        </p>
      )}
    </div>
  );
};

export default AdminDashboardPage;
